import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import {
  Eye, Plus, Edit2, Trash2, X, Save, Loader2, FileText,
  Download, Send, CheckCircle, Clock, RotateCcw, Printer,
  BarChart2, Settings, TrendingUp, TrendingDown, AlertTriangle,
  Bell, Users, Award, Activity, ChevronRight, RefreshCw,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
  LineChart, Line, Legend, ReferenceLine,
} from 'recharts';
import { observationsAPI, schoolConfigAPI } from '../services/api';
import { YearContext } from '../components/Layout/Layout';
import { useAuth } from '../context/AuthContext';
import { PageLoader } from '../components/Common/LoadingSpinner';
import StatCard from '../components/Cards/StatCard';
import EmailNotificationSetup    from '../components/Common/EmailNotificationSetup';
import TelegramNotificationSetup from '../components/Common/TelegramNotificationSetup';
import toast from 'react-hot-toast';

// ── Constants ──────────────────────────────────────────────
const CURRICULA = ['IB PYP', 'IGCSE', 'ICSE'];

const CRITERIA = [
  { key:'planning',    label:'Planning & Preparation',   indicator:'Clear learning objectives shared; lesson logically structured; aligned with curriculum outcomes.' },
  { key:'subject',     label:'Subject Knowledge',         indicator:'Confident, accurate command of content; concepts explained with clarity and depth.' },
  { key:'strategies',  label:'Teaching Strategies',       indicator:'Varied methods used; effective questioning; differentiation for diverse learners.' },
  { key:'engagement',  label:'Student Engagement',        indicator:'Active participation; curiosity sparked; opportunities for collaboration and discussion.' },
  { key:'environment', label:'Classroom Environment',     indicator:'Respectful, inclusive climate; clear routines; effective pace and time management.' },
  { key:'assessment',  label:'Assessment for Learning',   indicator:'Understanding checked regularly; constructive feedback given; formative tools used.' },
  { key:'resources',   label:'Resources & Technology',    indicator:'Resources and digital tools used purposefully to enhance learning.' },
];

const CURRICULUM_INDICATORS = {
  'IB PYP': [
    { key:'inquiry_led',         label:'Inquiry-led learning' },
    { key:'learner_profile',     label:'Learner Profile attributes evident' },
    { key:'atl_skills',          label:'ATL skills developed' },
    { key:'transdisciplinary',   label:'Transdisciplinary connections' },
    { key:'student_agency',      label:'Student agency & voice' },
  ],
  'IGCSE': [
    { key:'assessment_obj',      label:'Assessment objectives addressed' },
    { key:'critical_thinking',   label:'Critical thinking & analysis' },
    { key:'skill_application',   label:'Skill application' },
    { key:'exam_readiness',      label:'Exam-readiness embedded' },
    { key:'international_persp', label:'International perspective' },
  ],
  'ICSE': [
    { key:'conceptual_clarity',  label:'Conceptual clarity' },
    { key:'language_expression', label:'Strong language & expression' },
    { key:'knowledge_application',label:'Application of knowledge' },
    { key:'holistic_development',label:'Holistic development' },
    { key:'internal_assessment', label:'Internal assessment integration' },
  ],
};

// Follow-ups that REQUIRE additional notes — also the ONLY ones that SHOW notes field
const MANDATORY_NOTES_FOLLOWUPS = ['coaching', 'formal_review'];

const FOLLOW_UPS = [
  { value:'none',             label:'None' },
  { value:'informal_checkin', label:'Informal check-in' },
  { value:'coaching',         label:'Coaching session' },
  { value:'formal_review',    label:'Formal review' },
];

// ── Auto-compute overall rating from 7 criteria averages ──
function computeOverall(form) {
  const vals = [
    form.ratingPlanning, form.ratingSubject, form.ratingStrategies,
    form.ratingEngagement, form.ratingEnvironment, form.ratingAssessment, form.ratingResources,
  ].filter(v => v !== null && v !== undefined && v !== '');
  if (!vals.length) return { avg: null, rating: null, label: null };
  const avg = vals.reduce((a, b) => a + Number(b), 0) / vals.length;
  let rating, label, color;
  if (avg > 4)      { rating='excelling'; label='Excelling';  color='emerald'; }
  else if (avg > 3) { rating='proficient'; label='Proficient'; color='blue'; }
  else if (avg > 2) { rating='achieving';  label='Achieving';  color='indigo'; }
  else if (avg > 1) { rating='meeting';    label='Meeting';    color='amber'; }
  else              { rating='developing'; label='Developing'; color='red'; }
  return { avg: avg.toFixed(2), rating, label, color, filled: vals.length };
}

// ── Rating badge colours ───────────────────────────────────
const RATING_COLORS = {
  excelling:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  proficient: 'bg-blue-100 text-blue-800 border-blue-200',
  achieving:  'bg-purple-100 text-purple-800 border-purple-200',
  meeting:    'bg-amber-100 text-amber-800 border-amber-200',
  developing: 'bg-red-100 text-red-700 border-red-200',
};

// ── Rating button row ──────────────────────────────────────
// Selected style mirrors the Rating Scale legend: light fill + dark number per band
const RATING_CIRCLE = {
  5: 'bg-emerald-200 text-emerald-800',
  4: 'bg-blue-200 text-blue-800',
  3: 'bg-purple-200 text-purple-800',
  2: 'bg-amber-200 text-amber-800',
  1: 'bg-red-200 text-red-800',
};

function RatingRow({ value, onChange }) {
  return (
    <div className="flex gap-1.5 justify-center">
      {[5,4,3,2,1].map(n => (
        <button key={n} type="button" onClick={() => onChange(value === n ? null : n)}
          className={`w-9 h-9 rounded-full text-sm font-bold border-2 transition-all ${
            value === n
              ? `${RATING_CIRCLE[n]} border-transparent shadow-sm scale-110`
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-700'
          }`}>
          {n}
        </button>
      ))}
    </div>
  );
}

function SectionHead({ number, title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{number}</div>
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">{title}</h3>
    </div>
  );
}

// ── Sort arrow indicator ───────────────────────────────────
function SortArrow({ active, dir }) {
  if (!active) return <span className="ml-1 text-slate-300 font-normal">↕</span>;
  return <span className="ml-1 text-primary-500 font-normal">{dir === 'asc' ? '↑' : '↓'}</span>;
}

// ── Document-format PDF — strict 2-page layout ─────────────────────────
// Page 1: Header + Sections 1-3   (1123 px, overflow hidden, spacer fills gap)
// Page 2: Sections 4-6 + Sigs     (1123 px, overflow hidden, S5 boxes flex-fill)
function PDFReportContent({ obs, logoUrl }) {
  const ci = obs.curriculum_indicators
    ? (typeof obs.curriculum_indicators === 'string'
        ? JSON.parse(obs.curriculum_indicators)
        : obs.curriculum_indicators)
    : {};
  const avg              = obs.computed_avg ? Number(obs.computed_avg).toFixed(1) : null;
  const fmtDate          = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const teacherName      = obs.teacher_name_db  || obs.teacher_name  || '—';
  const observerName     = obs.observer_name_db || obs.observer_name || '—';
  const observerDes      = obs.observer_designation || '';
  const curriculum       = obs.curriculum || '';
  const gradeSec         = [obs.grade, obs.section ? `Sec ${obs.section}` : null].filter(Boolean).join(' / ') || '—';
  const needsFollowUpNotes = obs.follow_up === 'coaching' || obs.follow_up === 'formal_review';

  // Document-matching colour palette
  const P = {
    navy: '#1F4E79', blue: '#215E99', accent: '#2E75B6',
    lbl: '#D5E8F0', alt: '#F2F2F2', bd: '#BFBFBF',
    grey: '#808080', inst: '#595959',
    r5: '#C6E0B4', r4: '#FFF2CC', r3: '#FCE4D6', r2: '#F4B084', r1: '#DAE9F7',
  };
  const BD  = `1px solid ${P.bd}`;
  const TBL = { width: '100%', borderCollapse: 'collapse', fontSize: '11px' };
  // Label cell — centred horizontally, top-aligned
  const LC  = { background: P.lbl, fontWeight: '700', padding: '8px 10px', border: BD, textAlign: 'center', verticalAlign: 'top' };
  // Value cell — centred horizontally, top-aligned
  const VC  = { padding: '8px 10px', border: BD, color: '#1a1a1a', textAlign: 'center', verticalAlign: 'top' };
  // Alt label cell (grey)
  const AC  = { background: P.alt, fontWeight: '700', padding: '8px 10px', border: BD, textAlign: 'center', verticalAlign: 'top' };

  const chk = sel => sel ? '■' : '□';

  // Section heading (plain function returning JSX — avoids nested-component lint rule)
  const SH = (n, title) => (
    <div style={{
      fontSize: '12px', fontWeight: '700', color: P.navy, textTransform: 'uppercase',
      letterSpacing: '1.2px', borderBottom: `2.5px solid ${P.accent}`,
      padding: '0 0 4px 0', margin: '15px 0 7px 0',
    }}>
      SECTION {n}&nbsp;&nbsp;•&nbsp;&nbsp;{title}
    </div>
  );

  const rBg = { 5: P.r5, 4: P.r4, 3: P.r3, 2: P.r2, 1: P.r1 };

  return (
    <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", width: '794px', background: '#fff' }}>

      {/* ══════════════════════════ PAGE 1 ══════════════════════════════ */}
      {/* Header + S1 + S2 + S3 — spacer fills remaining height            */}
      <div style={{ height: '1123px', boxSizing: 'border-box', padding: '20px 36px 18px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ════ DOCUMENT HEADER ════════════════════════════════════ */}
        <div style={{ flexShrink: 0 }}>
          <table style={{ width: '100%', marginBottom: '6px' }}>
            <tbody><tr>
              <td style={{ width: '62px', verticalAlign: 'middle' }}>
                {logoUrl
                  ? <img src={logoUrl} alt="School Logo" style={{ width: '54px', height: '54px', objectFit: 'contain', display: 'block' }} />
                  : <div style={{ width: '54px', height: '54px', border: '1.5px solid #ccc', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
                      <span style={{ fontWeight: '800', fontSize: '11px', color: P.blue }}>TKS</span>
                    </div>
                }
              </td>
              <td style={{ textAlign: 'center', verticalAlign: 'middle', padding: '0 16px' }}>
                <div style={{ fontSize: '21px', fontWeight: '800', color: P.blue, letterSpacing: '2px', textTransform: 'uppercase' }}>Teacher Observation</div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: P.navy, marginTop: '2px', letterSpacing: '0.5px' }}>The Kulish School, Jaipur</div>
                <div style={{ fontSize: '10.5px', color: P.accent, marginTop: '3px', letterSpacing: '1px' }}>IB PYP &nbsp;&nbsp;•&nbsp;&nbsp; IGCSE &nbsp;&nbsp;•&nbsp;&nbsp; ICSE</div>
              </td>
              <td style={{ width: '62px' }}></td>
            </tr></tbody>
          </table>
          <div style={{ height: '2.5px', background: P.accent }}></div>
        </div>

        {/* ════ SECTION 1: BASIC INFORMATION ══════════════════════ */}
        <div style={{ flexShrink: 0 }}>
          {SH(1, 'Basic Information')}
          <table style={TBL}>
            <tbody>
              <tr>
                <td style={{ ...LC, width: '22%' }}>Teacher's Name</td>
                <td style={{ ...VC, width: '28%' }}>{teacherName}</td>
                <td style={{ ...LC, width: '17%' }}>Date</td>
                <td style={{ ...VC, width: '33%' }}>{fmtDate(obs.observation_date)}</td>
              </tr>
              <tr>
                <td style={LC}>Grade / Year</td>
                <td style={VC}>{gradeSec}</td>
                <td style={LC}>Time</td>
                <td style={VC}>{obs.time_of_obs || '—'}</td>
              </tr>
              <tr>
                <td style={LC}>Subject</td>
                <td style={VC}>{obs.subject_theme || '—'}</td>
                <td style={LC}>Duration</td>
                <td style={VC}>{obs.duration || '—'}</td>
              </tr>
              <tr>
                <td style={LC}>Topic / Unit</td>
                <td colSpan={3} style={VC}>{obs.topic_unit || '—'}</td>
              </tr>
              <tr>
                <td style={LC}>Observer's Name</td>
                <td style={VC}>{observerName}</td>
                <td style={LC}>Designation</td>
                <td style={VC}>{observerDes || '—'}</td>
              </tr>
              <tr>
                <td style={LC}>Curriculum</td>
                <td colSpan={3} style={{ ...VC }}>
                  {CURRICULA.map(cur => (
                    <span key={cur} style={{ marginRight: '28px', fontWeight: curriculum === cur ? '700' : 'normal', color: curriculum === cur ? P.navy : '#555' }}>
                      {chk(curriculum === cur)}&nbsp;&nbsp;{cur}
                    </span>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ════ SECTION 2: RATING SCALE ════════════════════════════ */}
        <div style={{ flexShrink: 0 }}>
          {SH(2, 'Rating Scale')}
          <table style={TBL}>
            <tbody><tr>
              {[{n:5,l:'5 — Excelling',bg:P.r5},{n:4,l:'4 — Proficient',bg:P.r4},{n:3,l:'3 — Achieving',bg:P.r3},{n:2,l:'2 — Meeting',bg:P.r2},{n:1,l:'1 — Developing',bg:P.r1}].map(r => (
                <td key={r.n} style={{ background: r.bg, border: BD, padding: 0, width: '20%', height: '42px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '42px', fontWeight: '700', fontSize: '11px', color: '#1a1a1a', textAlign: 'center' }}>{r.l}</div>
                </td>
              ))}
            </tr></tbody>
          </table>
        </div>

        {/* ════ SECTION 3: CORE OBSERVATION CRITERIA ══════════════ */}
        <div style={{ flexShrink: 0 }}>
          {SH(3, 'Core Observation Criteria')}
          <table style={{ ...TBL, fontSize: '10.5px' }}>
            <thead>
              <tr style={{ background: P.lbl }}>
                <th style={{ padding: '7px 5px', border: BD, textAlign: 'center', verticalAlign: 'middle', fontWeight: '700', width: '24px' }}>#</th>
                <th style={{ padding: '7px 8px', border: BD, textAlign: 'center', verticalAlign: 'middle', fontWeight: '700', width: '132px' }}>Criteria</th>
                <th style={{ padding: '7px 8px', border: BD, textAlign: 'center', verticalAlign: 'middle', fontWeight: '700' }}>Key Indicators</th>
                {[5,4,3,2,1].map(n => <th key={n} style={{ padding: '7px 3px', border: BD, textAlign: 'center', verticalAlign: 'middle', fontWeight: '700', width: '26px' }}>{n}</th>)}
              </tr>
            </thead>
            <tbody>
              {CRITERIA.map((cr, i) => {
                const rv  = obs[`rating_${cr.key}`] ? Number(obs[`rating_${cr.key}`]) : null;
                const rbg = i % 2 === 0 ? '#fff' : P.alt;
                return (
                  <tr key={cr.key}>
                    <td style={{ padding: '7px 5px', border: BD, textAlign: 'center', verticalAlign: 'top', color: '#888', background: rbg }}>{i+1}</td>
                    <td style={{ padding: '7px 8px', border: BD, fontWeight: '700', textAlign: 'center', verticalAlign: 'top', background: rbg }}>{cr.label}</td>
                    <td style={{ padding: '7px 8px', border: BD, fontSize: '10px', color: '#444', lineHeight: '1.55', textAlign: 'left', verticalAlign: 'top', background: rbg }}>{cr.indicator}</td>
                    {[5,4,3,2,1].map(col => (
                      <td key={col} style={{
                        padding: '7px 3px', border: BD, textAlign: 'center', verticalAlign: 'top',
                        background: rv === col ? rBg[col] : rbg,
                        fontSize: rv === col ? '14px' : '10.5px',
                        fontWeight: rv === col ? '700' : 'normal',
                        color: rv === col ? '#1a1a1a' : 'transparent',
                      }}>
                        {rv === col ? '✓' : ' '}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ════ SECTION 4: CURRICULUM-SPECIFIC FOCUS ══════════════ */}
        <div style={{ flexShrink: 0 }}>
          {SH(4, 'Curriculum-Specific Focus')}
          <p style={{ fontSize: '10px', fontStyle: 'italic', color: P.inst, margin: '-3px 0 5px 0' }}>Comment on the indicators relevant to the curriculum observed.</p>
          <table style={{ ...TBL, fontSize: '10.5px' }}>
            <tbody>
              {[
                { cur:'IB PYP', bg:P.lbl, txt:'Inquiry-led learning  •  Learner Profile attributes evident  •  ATL skills developed  •  Transdisciplinary connections  •  Student agency & voice' },
                { cur:'IGCSE',  bg:P.alt, txt:'Assessment objectives addressed  •  Critical thinking & analysis  •  Skill application  •  Exam-readiness embedded  •  International perspective' },
                { cur:'ICSE',   bg:P.lbl, txt:'Conceptual clarity  •  Strong language & expression  •  Application of knowledge  •  Holistic development  •  Internal assessment integration' },
              ].map(row => {
                const isObs   = curriculum === row.cur;
                const checked = isObs ? (CURRICULUM_INDICATORS[row.cur] || []).filter(ind => ci[ind.key]) : [];
                return (
                  <tr key={row.cur}>
                    <td style={{ background: row.bg, fontWeight: '700', padding: '8px 10px', border: BD, width: '78px', textAlign: 'center', verticalAlign: 'top', borderLeft: isObs ? `3.5px solid ${P.accent}` : BD }}>
                      {row.cur}
                      {isObs && <div style={{ fontSize: '9px', color: P.accent, fontWeight: '600', marginTop: '2px' }}>● Observed</div>}
                    </td>
                    <td style={{ padding: '8px 10px', border: BD, background: isObs ? '#f2f8fd' : '#fff', fontSize: '10.5px', lineHeight: '1.6', verticalAlign: 'top', color: isObs ? '#1a1a1a' : '#999' }}>
                      {isObs && checked.length > 0
                        ? (CURRICULUM_INDICATORS[row.cur] || []).map(ind => (
                            <span key={ind.key} style={{ marginRight: '14px', color: ci[ind.key] ? '#155724' : '#bbb' }}>
                              {ci[ind.key] ? '✓' : '☐'}&nbsp;{ind.label}
                            </span>
                          ))
                        : row.txt
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Spacer fills remaining page-1 height ──────────────── */}
        <div style={{ flex: 1, minHeight: 0 }}></div>

        {/* ── Page 1 footer ─────────────────────────────────────── */}
        <div style={{ flexShrink: 0, borderTop: `1px solid ${P.bd}`, paddingTop: '5px', display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: P.grey, fontStyle: 'italic' }}>
          <span>Teacher Observation Form &nbsp;•&nbsp; The Kulish School, Jaipur</span>
          <span>Page 1 of 2 &nbsp;•&nbsp; Confidential</span>
        </div>

      </div>{/* END PAGE 1 */}

      {/* ══════════════════════════ PAGE 2 ══════════════════════════════ */}
      {/* S5 (flex-fill) + S6 + Signatures + Footer                       */}
      <div style={{ height: '1123px', boxSizing: 'border-box', padding: '20px 36px 18px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ════ SECTION 5: REFLECTIVE COMMENTS ════════════════════ */}
        {/* flex:1 → fills all space between S4 and S6                */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flexShrink: 0 }}>{SH(5, 'Reflective Comments')}</div>
          {/* Three boxes each take equal share of remaining height    */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {[
              { label:'Strengths Observed',              value: obs.strengths,             bg: P.lbl },
              { label:'Areas for Development',            value: obs.areas_for_development, bg: P.alt },
              { label:'Specific, Actionable Suggestions', value: obs.suggestions,           bg: P.lbl },
            ].map(box => (
              <div key={box.label} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', border: BD }}>
                {/* Label row — centred */}
                <div style={{ flexShrink: 0, background: box.bg, fontWeight: '700', fontSize: '11px', padding: '7px 10px', borderBottom: BD, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {box.label}
                </div>
                {/* Content area — fills height, text left-aligned for readability */}
                <div style={{ flex: 1, padding: '8px 10px', fontSize: '11px', lineHeight: '1.65', whiteSpace: 'pre-wrap', textAlign: 'left', overflowY: 'hidden', color: box.value ? '#1a1a1a' : '#aaa', fontStyle: box.value ? 'normal' : 'italic' }}>
                  {box.value || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ════ SECTION 6: OVERALL ASSESSMENT ═════════════════════ */}
        <div style={{ flexShrink: 0 }}>
          {SH(6, 'Overall Assessment')}
          <table style={TBL}>
            <tbody>
              <tr>
                <td style={{ ...LC, width: '27%' }}>Overall Rating</td>
                <td style={{ ...VC, lineHeight: '2' }}>
                  {[{val:'excelling',lbl:'Excelling (5)'},{val:'proficient',lbl:'Proficient (4)'},{val:'achieving',lbl:'Achieving (3)'},{val:'meeting',lbl:'Meeting (2)'},{val:'developing',lbl:'Developing (1)'}].map(o => (
                    <span key={o.val} style={{ marginRight: '14px', fontWeight: obs.overall_rating === o.val ? '700' : 'normal', color: obs.overall_rating === o.val ? P.navy : '#666' }}>
                      {chk(obs.overall_rating === o.val)}&nbsp;&nbsp;{o.lbl}
                    </span>
                  ))}
                  {avg && <span style={{ marginLeft: '10px', color: '#888', fontSize: '10px' }}>(Avg: {avg}/5)</span>}
                </td>
              </tr>
              <tr>
                <td style={AC}>Follow-up Required</td>
                <td style={{ ...VC, background: '#fafafa', lineHeight: '2' }}>
                  {[{val:'none',lbl:'None'},{val:'informal_checkin',lbl:'Informal check-in'},{val:'coaching',lbl:'Coaching session'},{val:'formal_review',lbl:'Formal review'}].map(o => (
                    <span key={o.val} style={{ marginRight: '18px', fontWeight: obs.follow_up === o.val ? '700' : 'normal', color: obs.follow_up === o.val ? P.navy : '#666' }}>
                      {chk(obs.follow_up === o.val)}&nbsp;&nbsp;{o.lbl}
                    </span>
                  ))}
                </td>
              </tr>
              {needsFollowUpNotes && (
                <tr>
                  <td style={{ ...LC, background: '#EAF4FB' }}>
                    {obs.follow_up === 'coaching' ? 'Coaching Notes' : 'Formal Review Notes'}
                  </td>
                  <td style={{ padding: '7px 10px', border: BD, lineHeight: '1.65', whiteSpace: 'pre-wrap', verticalAlign: 'top', textAlign: 'left', color: obs.notes ? '#1a1a1a' : '#aaa', fontStyle: obs.notes ? 'normal' : 'italic' }}>
                    {obs.notes || '—'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ════ SIGNATURE BLOCK ════════════════════════════════════ */}
        <div style={{ flexShrink: 0 }}>
          <table style={{ width: '100%', marginTop: '16px' }}>
            <tbody><tr>
              <td style={{ width: '50%', paddingRight: '24px', verticalAlign: 'top' }}>
                <div style={{ height: '80px', marginBottom: '5px', borderBottom: '1.5px solid #666', display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  {obs.observer_signature_url && (
                    <img src={obs.observer_signature_url} alt="Observer Signature"
                      style={{ maxHeight: '72px', maxWidth: '240px', objectFit: 'contain' }} />
                  )}
                </div>
                <div style={{ fontSize: '9px', color: P.grey, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Observer's Signature</div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#1a1a1a', marginTop: '2px' }}>{observerName}</div>
                {observerDes && <div style={{ fontSize: '10px', color: '#555' }}>{observerDes}</div>}
              </td>
              <td style={{ width: '50%', paddingLeft: '24px', verticalAlign: 'top' }}>
                <div style={{ height: '80px', marginBottom: '5px', borderBottom: '1.5px solid #666', display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  {obs.teacher_signature_url && (
                    <img src={obs.teacher_signature_url} alt="Teacher Signature"
                      style={{ maxHeight: '72px', maxWidth: '240px', objectFit: 'contain' }} />
                  )}
                </div>
                <div style={{ fontSize: '9px', color: P.grey, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teacher's Signature (Acknowledgement)</div>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#1a1a1a', marginTop: '2px' }}>{teacherName}</div>
              </td>
            </tr></tbody>
          </table>
        </div>

        {/* ── Page 2 footer ─────────────────────────────────────── */}
        <div style={{ flexShrink: 0, marginTop: '12px', borderTop: `1px solid ${P.bd}`, paddingTop: '5px', display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: P.grey, fontStyle: 'italic' }}>
          <span>Teacher Observation Form &nbsp;•&nbsp; The Kulish School, Jaipur</span>
          <span>Page 2 of 2 &nbsp;•&nbsp; Confidential &nbsp;•&nbsp; For Internal Use Only</span>
        </div>

      </div>{/* END PAGE 2 */}

    </div>
  );
}

// ── Print-friendly single-observation view ─────────────────
function PrintView({ obs, onClose }) {
  const ci = obs.curriculum_indicators
    ? (typeof obs.curriculum_indicators === 'string' ? JSON.parse(obs.curriculum_indicators) : obs.curriculum_indicators) : {};
  const overall = obs.overall_rating;
  const avg     = obs.computed_avg ? Number(obs.computed_avg).toFixed(2) : '—';
  const gradeSection = [obs.grade, obs.section].filter(Boolean).join(' · Section ');

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto" id="print-area">
      <div className="max-w-3xl mx-auto p-8 print:p-4">
        {/* Close button — hidden on print */}
        <div className="flex justify-between items-center mb-6 print:hidden">
          <h1 className="text-xl font-bold text-slate-800">Teacher Observation Report</h1>
          <div className="flex gap-2">
            <button onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">
              <Printer size={14}/> Print / Save PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X size={18}/></button>
          </div>
        </div>

        {/* School header */}
        <div className="text-center border-b-2 border-slate-200 pb-4 mb-6">
          <h2 className="text-2xl font-bold text-slate-800">The Kulish School, Jaipur</h2>
          <p className="text-slate-500 text-sm mt-1">Teacher Observation Form · IB PYP · IGCSE · ICSE</p>
        </div>

        {/* Basic info grid */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm mb-6 border border-slate-200 rounded-xl p-4">
          {[
            ['Teacher', obs.teacher_name_db || obs.teacher_name],
            ['Observer', obs.observer_name_db || obs.observer_name],
            ['Observer Designation', obs.observer_designation],
            ['Date', obs.observation_date ? new Date(obs.observation_date).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : ''],
            ['Time', obs.time_of_obs],
            ['Duration', obs.duration],
            ['Grade / Section', gradeSection],
            ['Subject', obs.subject_theme],
            ['Curriculum', obs.curriculum],
            ['Topic / Unit', obs.topic_unit],
          ].map(([l,v]) => v ? (
            <div key={l} className="flex gap-2">
              <span className="text-slate-400 w-40 flex-shrink-0">{l}:</span>
              <span className="font-medium text-slate-700">{v}</span>
            </div>
          ) : null)}
        </div>

        {/* Core Criteria */}
        <div className="mb-6">
          <h3 className="font-bold text-slate-700 mb-3 text-sm uppercase tracking-wide">Core Observation Criteria</h3>
          <table className="w-full text-sm border border-slate-200 rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-primary-700 text-white text-xs">
                <th className="px-4 py-2 text-left">#</th>
                <th className="px-4 py-2 text-left">Criterion</th>
                <th className="px-4 py-2 text-center w-20">Rating</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {CRITERIA.map((c, i) => {
                const v = obs[`rating_${c.key}`];
                return (
                  <tr key={c.key} className={i%2===0?'bg-white':'bg-slate-50'}>
                    <td className="px-4 py-2 text-slate-400 text-xs">{i+1}</td>
                    <td className="px-4 py-2 font-medium text-slate-700">{c.label}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`inline-flex w-8 h-8 rounded-full text-sm font-bold items-center justify-center mx-auto ${
                        !v?'bg-slate-100 text-slate-300':v>=4?'bg-emerald-100 text-emerald-700':v===3?'bg-blue-100 text-blue-700':v===2?'bg-amber-100 text-amber-700':'bg-red-100 text-red-600'
                      }`}>{v||'—'}</span>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-50 font-semibold">
                <td colSpan={2} className="px-4 py-2 text-slate-700">Average Score</td>
                <td className="px-4 py-2 text-center font-bold text-primary-700">{avg}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Overall Rating */}
        <div className="mb-6 flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold tracking-wide">Overall Rating</p>
            <div className={`mt-1 inline-block px-4 py-1.5 rounded-full text-sm font-bold border capitalize ${RATING_COLORS[overall] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
              {overall || '—'}
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold tracking-wide">Follow-up Required</p>
            <p className="mt-1 text-sm font-medium text-slate-700 capitalize">{obs.follow_up?.replace('_',' ') || '—'}</p>
          </div>
        </div>

        {/* Curriculum indicators */}
        {obs.curriculum && Object.keys(ci).some(k => ci[k]) && (
          <div className="mb-6">
            <h3 className="font-bold text-slate-700 mb-2 text-sm uppercase tracking-wide">{obs.curriculum} Indicators Observed</h3>
            <div className="flex flex-wrap gap-2">
              {(CURRICULUM_INDICATORS[obs.curriculum] || []).filter(ind => ci[ind.key]).map(ind => (
                <span key={ind.key} className="text-xs bg-primary-50 text-primary-700 border border-primary-200 px-3 py-1 rounded-full">{ind.label}</span>
              ))}
            </div>
          </div>
        )}

        {/* Reflective comments */}
        {[
          {k:'strengths',        l:'Strengths Observed',                 v: obs.strengths},
          {k:'areas',            l:'Areas for Development',               v: obs.areas_for_development},
          {k:'suggestions',      l:'Specific, Actionable Suggestions',    v: obs.suggestions},
          {k:'notes',            l:'Additional Notes',                    v: obs.notes},
        ].filter(f=>f.v).map(f=>(
          <div key={f.k} className="mb-4">
            <h3 className="font-bold text-slate-700 mb-1 text-sm uppercase tracking-wide">{f.l}</h3>
            <p className="text-sm text-slate-600 bg-slate-50 rounded-xl p-3 border border-slate-100 whitespace-pre-wrap">{f.v}</p>
          </div>
        ))}

        {/* Signature block */}
        <div className="mt-10 grid grid-cols-2 gap-8 border-t border-slate-200 pt-6">
          <div>
            <div className="border-b border-slate-400 pb-1 mb-1 h-10"></div>
            <p className="text-xs text-slate-500">Observer's Signature</p>
            <p className="text-sm font-medium text-slate-700">{obs.observer_name_db || obs.observer_name}</p>
          </div>
          <div>
            <div className="border-b border-slate-400 pb-1 mb-1 h-10"></div>
            <p className="text-xs text-slate-500">Teacher's Signature (Acknowledgement)</p>
            <p className="text-sm font-medium text-slate-700">{obs.teacher_name_db || obs.teacher_name}</p>
          </div>
        </div>
      </div>
      <style>{`@media print { .print\\:hidden { display: none !important; } body { -webkit-print-color-adjust: exact; } }`}</style>
    </div>
  );
}

// ── View Observation Modal ─────────────────────────────────
function ViewObservationModal({ obs, logoUrl, onClose, onDownload, pdfGenerating }) {
  const teacherName = obs.teacher_name_db || obs.teacher_name || '—';
  const fmtD = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-6 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[880px]">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-2xl flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-bold text-slate-800">Observation Report</h2>
            <p className="text-xs text-slate-500 mt-0.5">{teacherName} &nbsp;·&nbsp; {fmtD(obs.observation_date)}</p>
          </div>
          <div className="flex items-center gap-2">
            {obs.status === 'published' && (
              <button onClick={onDownload} disabled={pdfGenerating}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <Download size={13}/> {pdfGenerating ? 'Generating…' : 'Download PDF'}
              </button>
            )}
            <button onClick={onClose} title="Close" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <X size={18}/>
            </button>
          </div>
        </div>
        {/* Report content — horizontally scrollable if viewport is narrow */}
        <div className="overflow-x-auto py-5 flex justify-center">
          <PDFReportContent obs={obs} logoUrl={logoUrl} />
        </div>
      </div>
    </div>
  );
}

// ── KPI Summary Drawer ─────────────────────────────────────
function KpiSummaryDrawer({ summary, onClose, onViewObs, onTeacherClick }) {
  const fmtD = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const count = summary.records.length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-lg max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-slate-800">{summary.title}</h3>
            {!summary.loading && (
              <p className="text-xs text-slate-400 mt-0.5">
                {summary.type === 'teachers'
                  ? `${count} teacher${count !== 1 ? 's' : ''}`
                  : `${count} observation${count !== 1 ? 's' : ''}`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors"><X size={18}/></button>
        </div>
        {/* Body */}
        <div className="overflow-y-auto p-4">
          {summary.loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-primary-500" size={28}/>
            </div>
          ) : count === 0 ? (
            <div className="text-center py-16">
              <AlertTriangle size={32} className="mx-auto text-slate-300 mb-3"/>
              <p className="text-slate-400 text-sm">No records found</p>
            </div>
          ) : summary.type === 'teachers' ? (
            <div className="space-y-2">
              {summary.records.map((r, i) => (
                <button key={i} onClick={() => onTeacherClick?.(r)}
                  className="w-full flex items-center justify-between p-3.5 bg-slate-50 hover:bg-indigo-50 rounded-xl transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Users size={15} className="text-indigo-500"/>
                    </div>
                    <span className="font-medium text-slate-700 group-hover:text-indigo-700 text-sm">{r.teacher_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-primary-600 bg-primary-50 group-hover:bg-white px-2.5 py-1 rounded-lg">{r.count} obs</span>
                    <ChevronRight size={14} className="text-slate-300 group-hover:text-indigo-400 transition-colors"/>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {summary.records.map(r => (
                <button key={r.id} onClick={() => onViewObs(r)}
                  className="w-full text-left p-3.5 bg-slate-50 hover:bg-primary-50 rounded-xl transition-colors group">
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <span className="font-semibold text-slate-800 text-sm group-hover:text-primary-700 transition-colors">
                      {r.teacher_name_db || r.teacher_name || '—'}
                    </span>
                    {r.overall_rating && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${RATING_COLORS[r.overall_rating] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                        {r.overall_rating}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-400">
                    <span>{fmtD(r.observation_date)}</span>
                    {r.subject_theme && <><span>·</span><span>{r.subject_theme}</span></>}
                    {r.grade && <><span>·</span><span>{r.grade}{r.section ? ` / Sec ${r.section}` : ''}</span></>}
                    {r.curriculum && <><span>·</span><span className="font-medium text-blue-500">{r.curriculum}</span></>}
                  </div>
                  {(r.observer_name_db || r.observer_name) && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                      <span className="text-slate-300">Observed by</span>
                      <span className="font-medium text-slate-500">
                        {r.observer_name_db || r.observer_name}
                        {r.observer_designation ? ` · ${r.observer_designation}` : ''}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Observation Form ───────────────────────────────────────
function ObservationForm({ initial, onSave, onCancel }) {
  const { user, isAdmin, isSuperAdmin, isManagement: isMgmtRole } = useAuth();
  const isManagement  = isAdmin || isSuperAdmin || isMgmtRole;
  const formNormRole  = (user?.role || '').toLowerCase().replace(/[\s-]+/g, '_');
  const isCoordForm   = formNormRole === 'coordinator';

  const [teachers,     setTeachers]     = useState([]);
  const [classes,      setClasses]      = useState([]);
  const [academicYears,setAcademicYears]= useState([]);
  const [allSubjects,  setAllSubjects]  = useState([]);
  const [subjects,     setSubjects]     = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [triedSubmit,  setTriedSubmit]  = useState(false);
  const [observersList, setObserversList] = useState([]);

  const nowTime = () => new Date().toTimeString().slice(0, 5);

  const blank = {
    teacherId:'', teacherName:'', observerId:'', observerName:'', observerDesignation:'',
    observationDate: new Date().toISOString().split('T')[0],
    subjectTheme:'', timeOfObs: nowTime(), grade:'', section:'', selectedClassId:'', academicYearId:'',
    duration:'', topicUnit:'', curriculum:'',
    ratingPlanning:null, ratingSubject:null, ratingStrategies:null,
    ratingEngagement:null, ratingEnvironment:null, ratingAssessment:null, ratingResources:null,
    curriculumIndicators:{}, strengths:'', areasForDevelopment:'', suggestions:'',
    followUp:'', notes:'',
  };

  const [form, setForm] = useState(() => {
    if (!initial) return blank;
    const ci = initial.curriculum_indicators
      ? (typeof initial.curriculum_indicators === 'string' ? JSON.parse(initial.curriculum_indicators) : initial.curriculum_indicators)
      : {};
    return {
      ...blank,
      teacherId: initial.teacher_id||'', teacherName: initial.teacher_name_db||initial.teacher_name||'',
      observerId: initial.observer_id||'', observerName: initial.observer_name_db||initial.observer_name||'',
      observerDesignation: initial.observer_designation||'',
      observationDate: initial.observation_date?.split('T')[0]||blank.observationDate,
      subjectTheme: initial.subject_theme||'', timeOfObs: initial.time_of_obs||'',
      grade: initial.grade||'', section: initial.section||'', selectedClassId:'',
      academicYearId: initial.academic_year_id ? String(initial.academic_year_id) : '',
      duration: initial.duration||'', topicUnit: initial.topic_unit||'',
      curriculum: initial.curriculum||'',
      ratingPlanning: initial.rating_planning||null, ratingSubject: initial.rating_subject||null,
      ratingStrategies: initial.rating_strategies||null, ratingEngagement: initial.rating_engagement||null,
      ratingEnvironment: initial.rating_environment||null, ratingAssessment: initial.rating_assessment||null,
      ratingResources: initial.rating_resources||null,
      curriculumIndicators: ci,
      strengths: initial.strengths||'', areasForDevelopment: initial.areas_for_development||'',
      suggestions: initial.suggestions||'', followUp: initial.follow_up||'', notes: initial.notes||'',
    };
  });

  const overall = computeOverall(form);
  const notesRequired = MANDATORY_NOTES_FOLLOWUPS.includes(form.followUp);

  useEffect(() => {
    const GRADE_ORDER = ['Nursery','KG 1','KG1','KG 2','KG2','I','II','III','IV','V','VI','VII','VIII'];
    const gradeRank = g => {
      const idx = GRADE_ORDER.findIndex(o => o.toLowerCase() === (g||'').toLowerCase());
      return idx === -1 ? 999 : idx;
    };
    const applyClasses = (allClasses, scopeGrades) => {
      const filtered = scopeGrades?.length
        ? allClasses.filter(c => scopeGrades.includes(c.grade))
        : allClasses;
      const sorted = [...filtered].sort((a, b) => {
        const diff = gradeRank(a.grade) - gradeRank(b.grade);
        if (diff !== 0) return diff;
        return (a.section||'').localeCompare(b.section||'');
      });
      setClasses(sorted);
      if (initial?.grade) {
        const match = sorted.find(c =>
          c.grade === initial.grade && (c.section || '') === (initial.section || '')
        );
        if (match) setForm(f => ({ ...f, selectedClassId: String(match.id) }));
      }
    };

    if (isCoordForm) {
      // Coordinator: fetch scope grades first, then filter classes + teachers to those grades
      observationsAPI.getScopeGrades().then(r => {
        const scopeGrades = r.data.grades || [];
        const gradesParam = scopeGrades.join(',');
        observationsAPI.getTeachersList(gradesParam ? { grades: gradesParam } : undefined)
          .then(t => setTeachers(t.data)).catch(() => {});
        schoolConfigAPI.getClasses().then(r2 => applyClasses(r2.data, scopeGrades)).catch(() => {});
      }).catch(() => {
        observationsAPI.getTeachersList().then(r => setTeachers(r.data)).catch(() => {});
        schoolConfigAPI.getClasses().then(r2 => applyClasses(r2.data, [])).catch(() => {});
      });
    } else {
      observationsAPI.getTeachersList().then(r => setTeachers(r.data)).catch(() => {});
      schoolConfigAPI.getClasses().then(r2 => applyClasses(r2.data, [])).catch(() => {});
    }
    schoolConfigAPI.getSubjects().then(r => { setAllSubjects(r.data); setSubjects(r.data); }).catch(() => {});
    schoolConfigAPI.getAcademicYears().then(r => {
      setAcademicYears(r.data);
      if (!initial) {
        const current = r.data.find(a => a.is_current);
        if (current) setForm(f => ({ ...f, academicYearId: String(current.id) }));
      }
    }).catch(() => {});

    if (isManagement) {
      observationsAPI.getObserversList().then(r => setObserversList(r.data)).catch(() => {});
    } else {
      observationsAPI.getMyObserverInfo().then(r => {
        const info = r.data;
        if (!initial) {
          setForm(f => ({
            ...f,
            observerId:          info.id   || '',
            observerName:        info.name || user?.name || '',
            observerDesignation: info.designation || '',
          }));
        }
      }).catch(() => {
        if (!initial) setForm(f => ({ ...f, observerName: user?.name || '' }));
      });
    }
  }, []); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // classLabel: "Grade V - A" display helper
  const classLabel = (cls) => cls.section ? `${cls.grade} - ${cls.section}` : cls.grade;

  const handleClassChange = (classId) => {
    if (!classId) {
      setForm(f => ({ ...f, grade: '', section: '', selectedClassId: '', subjectTheme: '' }));
      setSubjects(allSubjects);
      return;
    }
    const cls = classes.find(c => String(c.id) === String(classId));
    if (cls) {
      setForm(f => ({ ...f, grade: cls.grade || '', section: cls.section || '', selectedClassId: classId, subjectTheme: '' }));
      const filtered = allSubjects.filter(s => s.grades?.includes(cls.grade) || !s.grades?.length);
      setSubjects(filtered.length > 0 ? filtered : allSubjects);
    }
  };
  const handleTeacher  = (id) => { const e=teachers.find(t=>String(t.id)===String(id)); set('teacherId',id); if(e) set('teacherName',e.name); };
  const handleObserver = (id) => { const e=observersList.find(o=>String(o.id)===String(id)); set('observerId',id); if(e){set('observerName',e.name);set('observerDesignation',e.designation||'');} };
  const toggleInd = (k) => setForm(f=>({...f,curriculumIndicators:{...f.curriculumIndicators,[k]:!f.curriculumIndicators[k]}}));

  const validate = () => {
    setTriedSubmit(true);
    if (!form.teacherId && !form.teacherName) { toast.error('Teacher required'); return false; }
    if (!form.observerId && !form.observerName) { toast.error('Observer required'); return false; }
    if (!form.observationDate) { toast.error('Date required'); return false; }
    // All 7 criteria are mandatory
    const unrated = CRITERIA.filter(c => {
      const rk = `rating${c.key.charAt(0).toUpperCase()+c.key.slice(1)}`;
      return !form[rk];
    });
    if (unrated.length > 0) {
      toast.error(`Please rate all 7 criteria. Missing: ${unrated.map(c => c.label.split(' ')[0]).join(', ')}`);
      return false;
    }
    if (notesRequired && !form.notes?.trim()) {
      toast.error(`Additional Notes are mandatory when Follow-up is "${FOLLOW_UPS.find(f=>f.value===form.followUp)?.label}"`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (saveAction) => {
    if (!validate()) return;
    // No confirm needed — all 7 criteria are mandatory, validated above
    setSaving(true);
    try {
      const payload = {
        teacherId: form.teacherId||null, teacherName: form.teacherName,
        observerId: form.observerId||null, observerName: form.observerName,
        observerDesignation: form.observerDesignation, observationDate: form.observationDate,
        subjectTheme: form.subjectTheme, timeOfObs: form.timeOfObs,
        grade: form.grade, section: form.section, academicYearId: form.academicYearId||null,
        duration: form.duration, topicUnit: form.topicUnit, curriculum: form.curriculum,
        curriculumIndicators: form.curriculumIndicators,
        ratingPlanning: form.ratingPlanning, ratingSubject: form.ratingSubject,
        ratingStrategies: form.ratingStrategies, ratingEngagement: form.ratingEngagement,
        ratingEnvironment: form.ratingEnvironment, ratingAssessment: form.ratingAssessment,
        ratingResources: form.ratingResources,
        strengths: form.strengths, areasForDevelopment: form.areasForDevelopment,
        suggestions: form.suggestions, followUp: form.followUp,
        notes: notesRequired ? form.notes : '',  // only save notes for coaching/formal_review
        saveAction,
      };
      if (initial?.id) {
        const r = await observationsAPI.update(initial.id, payload);
        toast.success(r.data.status === 'published' ? '✅ Published!' : 'Draft saved');
      } else {
        const r = await observationsAPI.create(payload);
        toast.success(r.data.status === 'published' ? '✅ Published!' : 'Draft saved');
      }
      onSave();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const fld = 'text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 w-full';
  const ta  = `${fld} min-h-[100px] resize-y`;
  const currIndList = form.curriculum ? (CURRICULUM_INDICATORS[form.curriculum]||[]) : [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-start justify-center py-6 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl">

        {/* Header — part of scrollable content, not sticky */}
        <div className="bg-white rounded-t-2xl border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-800">Teacher Observation Form</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              The Kulish School · IB PYP · IGCSE · ICSE
              {initial?.status && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                  initial.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>{initial.status === 'published' ? '✓ Published' : 'Draft'}</span>
              )}
            </p>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded-xl text-slate-500"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-8">

          {/* S1: Basic Info */}
          <section>
            <SectionHead number="1" title="Basic Information" />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="text-xs font-medium text-slate-500 block mb-1">Teacher's Name *</label>
                <select className={fld} value={form.teacherId} onChange={e=>handleTeacher(e.target.value)}>
                  <option value="">Select teacher…</option>
                  {teachers.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {/* Observer — dropdown for admins, auto-filled read-only for regular users */}
              {isManagement ? (
                <>
                  <div>
                    <label className="text-xs font-medium text-slate-500 block mb-1">Observer's Name *</label>
                    <select className={fld} value={form.observerId} onChange={e=>handleObserver(e.target.value)}>
                      <option value="">Select observer…</option>
                      {observersList.map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 block mb-1">Observer's Designation</label>
                    <input className={fld} value={form.observerDesignation} onChange={e=>set('observerDesignation',e.target.value)} placeholder="e.g. HOD, Principal"/>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-medium text-slate-500 block mb-1">Observer's Name</label>
                    <div className={`${fld} bg-slate-50 text-slate-700`}>
                      <span className="font-medium">{form.observerName || user?.name || '—'}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 block mb-1">Observer's Designation</label>
                    <div className={`${fld} bg-slate-50 text-slate-600`}>
                      {form.observerDesignation || <span className="text-slate-400 italic">Loading…</span>}
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Date *</label>
                <input type="date" className={fld} value={form.observationDate} onChange={e=>set('observationDate',e.target.value)}/>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Time</label>
                <input type="time" className={fld} value={form.timeOfObs} onChange={e=>set('timeOfObs',e.target.value)}/>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Duration</label>
                <input className={fld} value={form.duration} onChange={e=>set('duration',e.target.value)} placeholder="e.g. 45 mins"/>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Grade / Section</label>
                <select className={fld} value={form.selectedClassId} onChange={e=>handleClassChange(e.target.value)}>
                  <option value="">Select class…</option>
                  {classes.map(cls=><option key={cls.id} value={cls.id}>{classLabel(cls)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Subject</label>
                <select className={fld} value={form.subjectTheme} onChange={e=>set('subjectTheme',e.target.value)}>
                  <option value="">Select subject…</option>
                  {subjects.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Academic Year</label>
                <select className={fld} value={form.academicYearId} onChange={e=>set('academicYearId',e.target.value)}>
                  <option value="">Select academic year…</option>
                  {academicYears.map(ay=><option key={ay.id} value={ay.id}>{ay.year_label}{ay.is_current?' (Current)':''}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 block mb-1">Topic / Unit</label>
                <input className={fld} value={form.topicUnit} onChange={e=>set('topicUnit',e.target.value)} placeholder="e.g. Fractions"/>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs font-medium text-slate-500 block mb-2">Curriculum</label>
              <div className="flex gap-3 flex-wrap">
                {CURRICULA.map(c => (
                  <label key={c} className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 cursor-pointer transition-all ${form.curriculum===c?'bg-primary-50 border-primary-500 text-primary-700':'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                    <input type="radio" name="curriculum" value={c} checked={form.curriculum===c} onChange={()=>set('curriculum',c)} className="accent-primary-600"/>
                    <span className="text-sm font-semibold">{c}</span>
                  </label>
                ))}
              </div>
            </div>
          </section>

          {/* S2: Rating Scale */}
          <section className="bg-slate-50 rounded-2xl p-5">
            <SectionHead number="2" title="Rating Scale" />
            <div className="grid grid-cols-5 gap-3">
              {[
                {n:5, l:'Excelling',  s:'4.1 – 5.0 · Highest performance', card:'bg-emerald-50 border-emerald-200', circle:'bg-emerald-200 text-emerald-800', label:'text-emerald-800', sub:'text-emerald-600'},
                {n:4, l:'Proficient', s:'3.1 – 4.0 · Above expected',      card:'bg-blue-50 border-blue-200',     circle:'bg-blue-200 text-blue-800',       label:'text-blue-800',    sub:'text-blue-500'},
                {n:3, l:'Achieving',  s:'2.1 – 3.0 · Meeting expected',    card:'bg-purple-50 border-purple-200', circle:'bg-purple-200 text-purple-800',   label:'text-purple-800',  sub:'text-purple-500'},
                {n:2, l:'Meeting',    s:'1.1 – 2.0 · Approaching standard',card:'bg-amber-50 border-amber-200',   circle:'bg-amber-200 text-amber-800',     label:'text-amber-800',   sub:'text-amber-600'},
                {n:1, l:'Developing', s:'0 – 1.0 · Below standard',        card:'bg-red-50 border-red-200',       circle:'bg-red-200 text-red-800',         label:'text-red-800',     sub:'text-red-500'},
              ].map(r=>(
                <div key={r.n} className={`text-center p-3 rounded-xl border shadow-sm ${r.card}`}>
                  <div className={`w-10 h-10 rounded-full ${r.circle} text-lg font-bold flex items-center justify-center mx-auto mb-1`}>{r.n}</div>
                  <p className={`text-xs font-bold ${r.label}`}>{r.l}</p>
                  <p className={`text-[10px] leading-tight mt-0.5 ${r.sub}`}>{r.s}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center italic">Tick the rating that best reflects observed practice for each criterion below. The overall rating is auto-calculated.</p>
          </section>

          {/* S3: Core Criteria */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</div>
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                Core Observation Criteria
                <span className="ml-2 text-red-500 font-semibold normal-case text-xs">* All 7 required</span>
              </h3>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary-700 text-white text-xs uppercase tracking-wide">
                    <th className="px-4 py-3 text-left w-8">#</th>
                    <th className="px-4 py-3 text-left w-40">Criteria</th>
                    <th className="px-4 py-3 text-left">Key Indicators</th>
                    <th className="px-4 py-3 text-center w-64">Rating (5 → 1)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {CRITERIA.map((c, idx) => {
                    const rk = `rating${c.key.charAt(0).toUpperCase()+c.key.slice(1)}`;
                    const isUnrated = triedSubmit && !form[rk];
                    return (
                      <tr key={c.key} className={`${isUnrated ? 'bg-red-50' : idx%2===0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                        <td className="px-4 py-4 text-slate-400 font-mono text-xs">{idx+1}</td>
                        <td className={`px-4 py-4 font-semibold ${isUnrated ? 'text-red-600' : 'text-slate-700'}`}>
                          {c.label}
                          {isUnrated && <span className="ml-1 text-red-400 text-xs">*</span>}
                        </td>
                        <td className="px-4 py-4 text-xs text-slate-500 leading-relaxed">{c.indicator}</td>
                        <td className="px-4 py-4"><RatingRow value={form[rk]} onChange={v=>setForm(f=>({...f,[rk]:v}))}/></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* S4: Curriculum Focus */}
          <section>
            <SectionHead number="4" title="Curriculum-Specific Focus" />
            {!form.curriculum ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center text-sm text-amber-700">Select a curriculum in Section 1 to see relevant indicators.</div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-5">
                <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-3">{form.curriculum} Indicators</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {currIndList.map(ind => (
                    <label key={ind.key} className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${form.curriculumIndicators[ind.key]?'bg-primary-50 border-primary-400 text-primary-700':'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                      <input type="checkbox" checked={!!form.curriculumIndicators[ind.key]} onChange={()=>toggleInd(ind.key)} className="accent-primary-600 w-4 h-4 flex-shrink-0"/>
                      <span className="text-sm font-medium">{ind.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* S5: Reflective Comments */}
          <section>
            <SectionHead number="5" title="Reflective Comments" />
            <div className="grid grid-cols-1 gap-5">
              {[
                {k:'strengths',label:'Strengths Observed',ph:'Describe the strengths observed…'},
                {k:'areasForDevelopment',label:'Areas for Development',ph:'Areas where the teacher can improve…'},
                {k:'suggestions',label:'Specific, Actionable Suggestions',ph:'Provide concrete suggestions…'},
              ].map(f=>(
                <div key={f.k}>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">{f.label}</label>
                  <textarea className={ta} value={form[f.k]} onChange={e=>set(f.k,e.target.value)} placeholder={f.ph} rows={4}/>
                </div>
              ))}
            </div>
          </section>

          {/* S6: Overall Assessment */}
          <section>
            <SectionHead number="6" title="Overall Assessment" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Auto-computed overall rating — read-only */}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-3">
                  Overall Rating <span className="normal-case font-normal text-slate-400">(auto-calculated from criteria average)</span>
                </label>
                {overall.rating ? (
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className={`px-5 py-2.5 rounded-full text-base font-bold border capitalize ${RATING_COLORS[overall.rating]}`}>
                      {overall.label}
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Average Score</p>
                      <p className="text-2xl font-bold text-slate-800">{overall.avg}<span className="text-sm font-normal text-slate-400"> / 5</span></p>
                      <p className="text-[11px] text-slate-400">{overall.filled}/7 criteria rated</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-dashed border-slate-300 text-slate-400 text-sm">
                    Rate the 7 criteria above — overall rating will appear here automatically.
                  </div>
                )}
                {/* Score scale legend */}
                <div className="mt-3 grid grid-cols-5 gap-1 text-[10px] text-center">
                  {[{r:'excelling',l:'Excelling',s:'4.1–5.0'},{r:'proficient',l:'Proficient',s:'3.1–4.0'},{r:'achieving',l:'Achieving',s:'2.1–3.0',cls:'bg-purple-100 text-purple-800 border-purple-200'},{r:'meeting',l:'Meeting',s:'1.1–2.0'},{r:'developing',l:'Developing',s:'0–1.0'}].map(x=>(
                    <div key={x.r} className={`rounded-lg py-1 px-1 border ${x.cls || RATING_COLORS[x.r]} ${overall.rating===x.r?'ring-2 ring-offset-1 ring-current':''}`}>
                      <div className="font-bold">{x.l}</div>
                      <div className="opacity-70">{x.s}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Follow-up */}
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-3">Follow-up Required</label>
                <div className="grid grid-cols-1 gap-2">
                  {FOLLOW_UPS.map(f=>(
                    <label key={f.value} className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all ${form.followUp===f.value?'bg-primary-50 border-primary-500 text-primary-700':'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                      <input type="radio" name="followUp" value={f.value} checked={form.followUp===f.value} onChange={()=>set('followUp',f.value)} className="accent-primary-600"/>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{f.label}</span>
                        {MANDATORY_NOTES_FOLLOWUPS.includes(f.value) && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold border border-amber-200">Notes required</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Notes — ONLY shown for Coaching / Formal review */}
            {notesRequired && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block mb-2">
                  Additional Notes
                  <span className="ml-2 text-red-600 font-bold">
                    * Required for {FOLLOW_UPS.find(f=>f.value===form.followUp)?.label}
                  </span>
                </label>
                <textarea
                  className={`${ta} ${!form.notes?.trim() ? 'border-red-300 focus:ring-red-400' : ''}`}
                  value={form.notes}
                  onChange={e=>set('notes',e.target.value)}
                  placeholder="Please describe the specific follow-up plan…"
                  rows={3}
                />
                {!form.notes?.trim() && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    ⚠ Additional notes are mandatory before publishing when follow-up is "{FOLLOW_UPS.find(f=>f.value===form.followUp)?.label}".
                  </p>
                )}
              </div>
            )}
          </section>

        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-between rounded-b-2xl">
          <p className="text-xs text-slate-400">
            {overall.rating
              ? `Overall: ${overall.label} (avg ${overall.avg}/5 · ${overall.filled}/7 criteria rated)`
              : ''}
          </p>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-4 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-white text-slate-600">Cancel</button>
            <button onClick={() => handleSubmit('draft')} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60">
              <Save size={13}/> Save Draft
            </button>
            <button onClick={() => handleSubmit('publish')} disabled={saving}
              className="flex items-center gap-1.5 px-5 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg disabled:opacity-60 shadow-sm">
              <Send size={13}/> Publish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
// ── Recharts custom tooltip ────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-xl px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 mb-1 truncate max-w-[180px]">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

// ── Rating colour palette — matches the 5-point scale ──────
// 5 Excelling (4.1–5.0): green  |  4 Proficient (3.1–4.0): blue
// 3 Achieving (2.1–3.0): purple |  2 Meeting (1.1–2.0): amber
// 1 Developing (0–1.0): red
// `min` is the exclusive lower bound (band = min < score); e.g. Excelling = score > 4
const SCORE_PALETTE = [
  { min: 4, label: 'Excelling',  hex: '#22c55e', bg: 'bg-emerald-50', border: 'border-emerald-200', txt: 'text-emerald-700', circle: 'bg-emerald-100 text-emerald-700' },
  { min: 3, label: 'Proficient', hex: '#3b82f6', bg: 'bg-blue-50',    border: 'border-blue-200',    txt: 'text-blue-700',    circle: 'bg-blue-100 text-blue-700'       },
  { min: 2, label: 'Achieving',  hex: '#8b5cf6', bg: 'bg-purple-50',  border: 'border-purple-200',  txt: 'text-purple-700',  circle: 'bg-purple-100 text-purple-700'   },
  { min: 1, label: 'Meeting',    hex: '#f59e0b', bg: 'bg-amber-50',   border: 'border-amber-200',   txt: 'text-amber-700',   circle: 'bg-amber-100 text-amber-700'     },
  { min: 0, label: 'Developing', hex: '#ef4444', bg: 'bg-red-50',     border: 'border-red-200',     txt: 'text-red-600',     circle: 'bg-red-100 text-red-600'         },
];

function scorePalette(score) {
  const n = Number(score);
  if (!score && score !== 0) return SCORE_PALETTE[4]; // no score → grey fallback shown below
  return SCORE_PALETTE.find(p => n > p.min) || SCORE_PALETTE[4];
}

function scoreColor(score) {
  if (!score && score !== 0) return '#94a3b8';
  return scorePalette(score).hex;
}

// ── Small bar chart component ──────────────────────────────
function ObsBarChart({ data, dataKey = 'count', nameKey = 'name', label = 'Count', height = 240 }) {
  if (!data?.length) return (
    <div className="flex items-center justify-center h-40 text-slate-400 text-sm">No data yet</div>
  );
  const trimmed = data.slice(0, 20);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={trimmed} margin={{ top: 4, right: 8, left: -20, bottom: 4 }} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9"/>
        <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false}/>
        <YAxis
          dataKey={nameKey} type="category" tick={{ fontSize: 10 }} width={110}
          tickLine={false} axisLine={false}
          tickFormatter={v => v?.length > 16 ? v.slice(0, 15) + '…' : (v || '—')}
        />
        <Tooltip content={<ChartTooltip/>}/>
        <Bar dataKey={dataKey} name={label} radius={[0, 4, 4, 0]} maxBarSize={18}>
          {trimmed.map((entry, i) => (
            <Cell key={i} fill={scoreColor(entry.avg_score)}/>
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Colour legend for charts ───────────────────────────────
function ScoreLegend() {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
      {SCORE_PALETTE.map(p => (
        <span key={p.label} className="flex items-center gap-1 text-[10px] text-slate-500">
          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: p.hex }}/>
          {p.label}
        </span>
      ))}
    </div>
  );
}

// ── Distinct color palette for grade lines ─────────────────
const GRADE_COLORS = [
  '#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6',
  '#ec4899','#14b8a6','#f97316','#8b5cf6','#84cc16',
];

// ── Monthly Grade-wise Performance Line Chart ───────────────
function MonthlyGradeTrendChart({ raw }) {
  if (!raw?.length) return (
    <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
      No monthly data yet
    </div>
  );

  // Pivot: [{ month:'2026-05', grade:'IV', avg_score:3.2 }, ...] →
  //        [{ label:'May 26', IV:3.2, VI:2.9 }, ...]
  const months  = [...new Set(raw.map(r => r.month))].sort();
  const grades  = [...new Set(raw.map(r => r.grade))].sort((a, b) => {
    const numA = parseInt(a, 10), numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  const fmtMonth = m => {
    const [y, mo] = m.split('-');
    const label = new Date(Number(y), Number(mo) - 1, 1)
      .toLocaleString('en-IN', { month: 'short' });
    return `${label} '${String(y).slice(2)}`;
  };

  const pivoted = months.map(month => {
    const row = { label: fmtMonth(month) };
    grades.forEach(g => {
      const found = raw.find(r => r.month === month && r.grade === g);
      row[g] = found ? Number(found.avg_score) : null;
    });
    return row;
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs">
        <p className="font-semibold text-slate-700 mb-2">{label}</p>
        {payload.map(entry => entry.value != null && (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: entry.color }}/>
              <span className="text-slate-600">Grade {entry.dataKey}</span>
            </span>
            <span className="font-bold" style={{ color: entry.color }}>
              {Number(entry.value).toFixed(2)} / 5
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={pivoted} margin={{ top: 8, right: 16, left: -16, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
        <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false}/>
        <YAxis domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false}/>
        <ReferenceLine y={3} stroke="#94a3b8" strokeDasharray="4 2" label={{ value:'Proficient', position:'insideTopRight', fontSize:9, fill:'#94a3b8'}}/>
        <Tooltip content={<CustomTooltip/>}/>
        <Legend
          iconType="circle" iconSize={8}
          formatter={v => <span className="text-[11px] text-slate-600">Grade {v}</span>}
        />
        {grades.map((g, i) => (
          <Line
            key={g} dataKey={g} name={g}
            stroke={GRADE_COLORS[i % GRADE_COLORS.length]}
            strokeWidth={2.5} dot={{ r: 4, strokeWidth: 0 }}
            activeDot={{ r: 6 }} connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Teacher observation-by-observation performance chart ───
const TEACHER_LINE_COLORS = [
  '#6366f1','#f59e0b','#10b981','#ef4444','#3b82f6',
  '#ec4899','#14b8a6','#f97316','#8b5cf6','#84cc16',
  '#06b6d4','#a855f7','#64748b','#d97706','#16a34a',
  '#dc2626','#2563eb','#db2777','#0d9488','#ca8a04',
];

function TeacherObsTrendChart({ raw, isManagement }) {
  // ── All hooks unconditional — BEFORE any early return (Rules of Hooks) ──
  const [selected, setSelected] = React.useState(() => new Set());

  React.useEffect(() => {
    if (!raw?.length) return;
    const map = {};
    raw.forEach(r => {
      if (!map[r.teacher]) map[r.teacher] = [];
      map[r.teacher].push(r);
    });
    const rnk = Object.keys(map).sort((a, b) => map[b].length - map[a].length);
    setSelected(new Set(rnk.slice(0, 12)));
  }, [raw]);

  // ── Early return (after all hooks) ──
  if (!raw?.length) return (
    <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
      No observations yet
    </div>
  );

  // Group observations per teacher, ordered by date (already sorted from backend)
  const byTeacher = {};
  raw.forEach(r => {
    if (!byTeacher[r.teacher]) byTeacher[r.teacher] = [];
    byTeacher[r.teacher].push({ score: Number(r.avg_score), date: r.obs_label });
  });

  // Rank by total observation count descending
  const ranked = Object.keys(byTeacher).sort(
    (a, b) => byTeacher[b].length - byTeacher[a].length
  );

  // Guard: if useEffect hasn't fired yet (first render), default to top 12
  const safeSelected = selected.size > 0 ? selected : new Set(ranked.slice(0, 12));

  const toggle = t => setSelected(prev => {
    const base = prev.size > 0 ? prev : new Set(ranked.slice(0, 12));
    const s = new Set(base);
    if (s.has(t)) { if (s.size > 1) s.delete(t); }
    else s.add(t);
    return s;
  });

  // X-axis: "Obs 1", "Obs 2", … up to the max obs count across all teachers
  const maxObs = Math.max(...Object.values(byTeacher).map(a => a.length));
  const pivoted = Array.from({ length: maxObs }, (_, i) => {
    const row = { label: `Obs ${i + 1}` };
    ranked.forEach(t => {
      row[t]            = byTeacher[t][i]?.score ?? null;
      row[`${t}__date`] = byTeacher[t][i]?.date  ?? null;
    });
    return row;
  });

  // Custom dot — colored by observation score rating
  const RatingDot = (props) => {
    const { cx, cy, value } = props;
    if (value == null || isNaN(cx) || isNaN(cy)) return null;
    const rp = scorePalette(Number(value));
    return <circle cx={cx} cy={cy} r={6} fill={rp.hex} stroke="white" strokeWidth={2}/>;
  };

  const RatingActiveDot = (props) => {
    const { cx, cy, value } = props;
    if (value == null || isNaN(cx) || isNaN(cy)) return null;
    const rp = scorePalette(Number(value));
    return (
      <>
        <circle cx={cx} cy={cy} r={11} fill={rp.hex} fillOpacity={0.18}/>
        <circle cx={cx} cy={cy} r={7}  fill={rp.hex} stroke="white" strokeWidth={2}/>
      </>
    );
  };

  // Tooltip — shows teacher, score+rating, date
  const ObsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const visible = payload.filter(e => e.value != null);
    if (!visible.length) return null;
    const idx = pivoted.findIndex(r => r.label === label);
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs min-w-[200px]">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">{label}</p>
        {visible.map(entry => {
          const rp   = scorePalette(Number(entry.value));
          const date = idx >= 0 ? pivoted[idx][`${entry.dataKey}__date`] : null;
          return (
            <div key={entry.dataKey} className="py-1.5 border-b border-slate-100 last:border-0">
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.stroke }}/>
                  <span className="font-semibold text-slate-700 truncate">{entry.dataKey}</span>
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: rp.hex }}/>
                  <span className="font-bold" style={{ color: rp.hex }}>
                    {Number(entry.value).toFixed(2)}/5
                  </span>
                </span>
              </div>
              <div className="flex items-center justify-between mt-0.5 pl-4">
                <span className="text-[9px] font-semibold" style={{ color: rp.hex }}>{rp.label}</span>
                {date && <span className="text-[9px] text-slate-400">{date}</span>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* Rating colour legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3 pb-3 border-b border-slate-100">
        {SCORE_PALETTE.map(p => (
          <span key={p.label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className="w-3 h-3 rounded-full" style={{ background: p.hex }}/>
            <span className="font-medium" style={{ color: p.hex }}>{p.label}</span>
            <span className="text-slate-400">≥ {p.min}</span>
          </span>
        ))}
        <span className="ml-auto text-[10px] text-slate-400 italic">
          {isManagement ? 'School-wide' : 'Your observed teachers'}
        </span>
      </div>

      {/* Teacher toggle pills */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button
          onClick={() => setSelected(new Set(ranked))}
          className="text-[10px] px-2.5 py-1 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100 transition-colors"
        >
          All
        </button>
        <button
          onClick={() => setSelected(new Set([ranked[0]]))}
          className="text-[10px] px-2.5 py-1 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100 transition-colors"
        >
          Clear
        </button>
        {ranked.map((t, i) => {
          const clr = TEACHER_LINE_COLORS[i % TEACHER_LINE_COLORS.length];
          const on  = safeSelected.has(t);
          return (
            <button
              key={t}
              onClick={() => toggle(t)}
              title={`${t} · ${byTeacher[t].length} obs`}
              className="text-[10px] px-2.5 py-1 rounded-full border transition-all"
              style={on
                ? { background: clr, borderColor: clr, color: '#fff' }
                : { background: '#fff', borderColor: '#e2e8f0', color: '#64748b' }
              }
            >
              {t}
            </button>
          );
        })}
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={pivoted} margin={{ top: 8, right: 24, left: -16, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
          <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false}/>
          <YAxis domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 10 }} tickLine={false} axisLine={false}/>
          <ReferenceLine y={3} stroke="#94a3b8" strokeDasharray="4 2"
            label={{ value: 'Proficient', position: 'insideTopRight', fontSize: 9, fill: '#94a3b8' }}
          />
          <Tooltip content={<ObsTooltip/>}/>
          {ranked.map((t, i) => safeSelected.has(t) && (
            <Line
              key={t} dataKey={t} name={t}
              stroke={TEACHER_LINE_COLORS[i % TEACHER_LINE_COLORS.length]}
              strokeWidth={2} strokeOpacity={0.55}
              dot={<RatingDot/>}
              activeDot={<RatingActiveDot/>}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Teacher rank card — percentage + avg score ─────────────
function TeacherRankCard({ rank, name, avg, count, pct, variant = 'top' }) {
  const p        = avg ? scorePalette(avg) : (variant === 'top' ? SCORE_PALETTE[0] : SCORE_PALETTE[4]);
  const score    = avg  ? Number(avg).toFixed(1)  : '—';
  const pctVal   = pct  ? Number(pct).toFixed(0)  : null;
  const barClr   = variant === 'top' ? 'bg-emerald-500' : 'bg-red-500';
  const pctLabel = variant === 'top' ? '% scored > 3' : '% scored < 3';

  return (
    <div className={`p-3 rounded-xl border ${p.bg} ${p.border}`}>
      {/* Top row: rank + name + percentage */}
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${p.circle}`}>
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{name}</p>
          <p className="text-[10px] text-slate-400">{count} observation{count !== 1 ? 's' : ''}</p>
        </div>
        {pctVal !== null && (
          <div className="text-right flex-shrink-0">
            <div className={`text-lg font-bold leading-none ${p.txt}`}>{pctVal}%</div>
            <div className="text-[9px] text-slate-400 mt-0.5">{pctLabel}</div>
          </div>
        )}
      </div>

      {/* Progress bar + avg score */}
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 h-2 bg-white/70 rounded-full overflow-hidden border border-white/50">
          <div
            className={`h-full rounded-full transition-all ${barClr}`}
            style={{ width: `${Math.min(100, pctVal || 0)}%` }}
          />
        </div>
        <span className={`text-[10px] font-semibold flex-shrink-0 ${p.txt}`}>
          {score}/5 · {p.label}
        </span>
      </div>
    </div>
  );
}

// ── Dashboard tab ──────────────────────────────────────────
function ObsDashboard({ yearId, isManagement, preloadedDash, preloadedPending }) {
  const [dash,    setDash]    = useState(preloadedDash    || null);
  const [pending, setPending] = useState(preloadedPending || null);
  const [loading, setLoading] = useState(!preloadedDash);

  // Sync if parent updates the pre-loaded data
  useEffect(() => { if (preloadedDash)    setDash(preloadedDash);    }, [preloadedDash]);
  useEffect(() => { if (preloadedPending) setPending(preloadedPending); }, [preloadedPending]);

  // If not pre-loaded (observer login — no parent data), load independently
  const load = useCallback(async () => {
    if (preloadedDash) return; // already provided by parent
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        observationsAPI.getDashboard({ yearId }),
        observationsAPI.getPending({ yearId }),
      ]);
      setDash(d.data);
      setPending(p.data);
    } catch(e) { console.error('dashboard:', e); }
    setLoading(false);
  }, [yearId, preloadedDash]);

  useEffect(() => { load(); }, [load]);

  if (loading || !dash) return <PageLoader/>;

  // Admin: count of observers who still have pending obs this week
  // Observer: own remaining count (backend already filters to their record only)
  const pendingCount = isManagement
    ? (pending?.pending?.filter(p => p.remaining > 0).length || 0)
    : (pending?.pending?.[0]?.remaining ?? 0);
  const fmtD = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';

  return (
    <div className="space-y-5">

      {/* Quick numbers row — observer only (admin sees these in the main overview above) */}
      {!isManagement && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'This Week',        value: dash?.thisWeek  ?? '—', icon: Activity,      color: 'text-blue-600',   bg: 'bg-blue-50'   },
            { label: 'This Month',       value: dash?.thisMonth ?? '—', icon: BarChart2,     color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Pending Obs.',     value: pendingCount,           icon: AlertTriangle, color: 'text-amber-600',  bg: 'bg-amber-50'  },
            { label: 'Last Observation', value: fmtD(dash?.myLastObs),  icon: Clock,         color: 'text-slate-600',  bg: 'bg-slate-100' },
          ].map(s => (
            <div key={s.label} className="glass-card p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.bg}`}>
                <s.icon size={18} className={s.color}/>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-400 font-medium">{s.label}</p>
                <p className={`font-bold text-slate-800 mt-0.5 ${s.label === 'Last Observation' ? 'text-sm leading-tight' : 'text-2xl'}`}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Teacher-wise Observation Performance Trend */}
      <div className="glass-card p-5">
        <div className="mb-1">
          <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <TrendingUp size={14} className="text-indigo-500"/>
            {isManagement ? 'Teacher-wise Performance Trend' : 'Your Teachers — Performance Trend'}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {isManagement
              ? 'School-wide · score per observation · hover for date · dashed = Proficient (3.0)'
              : 'Only teachers you have observed · score per observation · hover for date'}
          </p>
        </div>
        <TeacherObsTrendChart raw={dash?.teacherObsTrend} isManagement={isManagement}/>
      </div>

      {/* Top performers / Needs support */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-emerald-500"/>
            <h3 className="text-sm font-bold text-slate-700">Top Performers</h3>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 ml-auto">
              ≥ 50% scored &gt; 3
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mb-3">Teachers where half or more observations are Proficient / Excelling</p>
          <div className="space-y-2">
            {dash?.top5?.length
              ? dash.top5.map((t, i) => (
                  <TeacherRankCard
                    key={t.name} rank={i + 1} name={t.name}
                    avg={t.avg_score} count={Number(t.count)}
                    pct={t.high_pct} variant="top"
                  />
                ))
              : <p className="text-sm text-slate-400 text-center py-6">No teacher has ≥ 50% observations scored above 3 yet</p>
            }
          </div>
        </div>

        <div className="glass-card p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown size={14} className="text-red-500"/>
            <h3 className="text-sm font-bold text-slate-700">Needs Support</h3>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600 ml-auto">
              ≥ 50% scored &lt; 3
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mb-3">Teachers where half or more observations are Developing / Meeting</p>
          <div className="space-y-2">
            {dash?.bottom5?.length
              ? dash.bottom5.map((t, i) => (
                  <TeacherRankCard
                    key={t.name} rank={i + 1} name={t.name}
                    avg={t.avg_score} count={Number(t.count)}
                    pct={t.low_pct} variant="bottom"
                  />
                ))
              : <p className="text-sm text-slate-400 text-center py-6">No teacher has ≥ 50% observations below 3 🎉</p>
            }
          </div>
        </div>
      </div>

      {/* Pending observations */}
      {isManagement && pending?.pending?.length > 0 && (
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <AlertTriangle size={14} className="text-amber-500"/>
              Weekly Pending Observations
            </h3>
            <span className="text-[10px] text-slate-400">
              Week: {pending.weekStart} → {pending.weekEnd}
              &nbsp;·&nbsp;Required per observer: <strong>{pending.weeklyRequired}</strong>
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pending.pending.map(obs => {
              const done = obs.remaining === 0;
              return (
                <div key={obs.name} className={`flex items-center gap-3 p-3 rounded-xl border ${done ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {done ? '✓' : obs.remaining}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{obs.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${done ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${Math.min(100, (obs.done / obs.required) * 100)}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-500 whitespace-nowrap">{obs.done}/{obs.required}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}

// ── Setup tab (admin only) ─────────────────────────────────
function ObsSetup({ yearId }) {
  const [config,    setConfig]    = useState(null);
  const [local,     setLocal]     = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [sending,   setSending]   = useState(false);
  const [loading,   setLoading]   = useState(true);

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    observationsAPI.getConfig({ yearId })
      .then(r => { setConfig(r.data); setLocal(r.data); })
      .catch(() => toast.error('Failed to load config'))
      .finally(() => setLoading(false));
  }, [yearId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await observationsAPI.saveConfig({ ...local, academic_year_id: yearId });
      setConfig(local);
      toast.success('✅ Settings saved!');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleSendAlert = async () => {
    setSending(true);
    try {
      const r = await observationsAPI.sendTelegramAlert({ yearId });
      const d = r.data;
      if (d.pendingCount === 0) {
        toast.success('✅ All observers are up to date — completion message sent to group.');
      } else {
        const noTgList = d.noTelegram?.length ? `\n⚠️ No Telegram ID: ${d.noTelegram.join(', ')}` : '';
        toast.success(`📲 ${d.notifiedDirect} personal + group alert sent! (${d.pendingCount} pending)${noTgList}`, { duration: 6000 });
      }
    } catch(e) { toast.error(e.response?.data?.error || 'Telegram send failed'); }
    setSending(false);
  };

  if (loading) return <PageLoader/>;
  if (!local) return null;

  const dirty = JSON.stringify(local) !== JSON.stringify(config);

  return (
    <div className="max-w-2xl space-y-6">

      <div className="glass-card p-6 space-y-5">
        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
          <Settings size={16} className="text-primary-500"/>Observation Frequency
        </h3>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Weekly observations required per observer
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number" min={1} max={20}
              value={local.weekly_required_per_observer || 1}
              onChange={e => setLocal(l => ({ ...l, weekly_required_per_observer: Number(e.target.value) }))}
              className="w-24 text-center text-lg font-bold border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <span className="text-sm text-slate-500">observations / week / observer</span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Each active observer must complete this many observations every week.
          </p>
        </div>
      </div>

      <div className="glass-card p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Bell size={16} className="text-amber-500"/>Telegram Notification Schedule
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setLocal(l => ({ ...l, telegram_enabled: l.telegram_enabled ? 0 : 1 }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${local.telegram_enabled ? 'bg-primary-500' : 'bg-slate-300'}`}
            >
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${local.telegram_enabled ? 'translate-x-5' : 'translate-x-0'}`}/>
            </div>
            <span className="text-sm font-medium text-slate-700">
              {local.telegram_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>

        <div className={`space-y-4 transition-opacity ${local.telegram_enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Notification Day</label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map(day => (
                <button
                  key={day}
                  onClick={() => setLocal(l => ({ ...l, notification_day: day }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    local.notification_day === day
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {day.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Notification Time</label>
            <input
              type="time"
              value={local.notification_time || '17:00'}
              onChange={e => setLocal(l => ({ ...l, notification_time: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="text-xs text-slate-400 mt-1">
              Alert will be sent at this time on the selected day if any observer hasn't met the weekly target.
              Only sends once per week.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <button
            onClick={handleSendAlert}
            disabled={sending}
            className="flex items-center gap-2 px-4 py-2 border border-amber-300 bg-amber-50 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
          >
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            Send Test Alert Now
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-40"
          >
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>}
            Save Settings
          </button>
        </div>
      </div>

      {/* ── Schedule diagnostics panel ─────────────────────── */}
      <div className="glass-card p-5 space-y-3">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Activity size={14} className="text-slate-400"/> Schedule Status
        </h3>
        <div className="grid grid-cols-2 gap-3 text-xs">
          {[
            { label: 'Server Date/Time',  value: config?._serverTime },
            { label: 'Server Day',        value: config?._serverDay  },
            { label: 'Configured Day',    value: config?.notification_day },
            { label: 'Configured Time',   value: config?.notification_time },
            { label: 'This Week (ISO)',   value: config?._thisWeek },
            { label: 'Last Sent Week',    value: config?.last_notified_week || 'Never' },
          ].map(row => (
            <div key={row.label} className="bg-slate-50 rounded-lg p-2.5">
              <p className="text-slate-400 mb-0.5">{row.label}</p>
              <p className="font-semibold text-slate-700">{row.value || '—'}</p>
            </div>
          ))}
        </div>

        {/* Status badge */}
        {config && (
          <div className={`flex items-center gap-2 p-3 rounded-xl text-xs font-medium border ${
            config._alreadySentThisWeek
              ? 'bg-amber-50 border-amber-200 text-amber-700'
              : config._serverDay === config.notification_day
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-slate-50 border-slate-200 text-slate-500'
          }`}>
            {config._alreadySentThisWeek
              ? <><AlertTriangle size={12}/> Already sent this week ({config.last_notified_week}) — will fire again next {config.notification_day}. Click "Save Settings" to reset and allow re-send.</>
              : config._serverDay === config.notification_day
              ? <><CheckCircle size={12}/> Today is {config.notification_day} — cron will fire within ±15 min of {config.notification_time}</>
              : <><Clock size={12}/> Waiting for {config.notification_day} at {config.notification_time} (server is currently {config._serverDay})</>
            }
          </div>
        )}
      </div>

    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// My Feedback — observations about this teacher
// ══════════════════════════════════════════════════════════════
const OBS_CRITERIA = [
  { key:'rating_planning',    label:'Planning'     },
  { key:'rating_subject',     label:'Subject Kn.'  },
  { key:'rating_strategies',  label:'Strategies'   },
  { key:'rating_engagement',  label:'Engagement'   },
  { key:'rating_environment', label:'Environment'  },
  { key:'rating_assessment',  label:'Assessment'   },
  { key:'rating_resources',   label:'Resources'    },
];

function ObsAboutMeCard({ obs }) {
  const avg     = obs.computed_avg ? Number(obs.computed_avg).toFixed(2) : null;
  const ratingCls = RATING_COLORS[obs.overall_rating] || 'bg-slate-100 text-slate-700 border-slate-200';
  const fmtDate   = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
  const followUp  = FOLLOW_UPS.find(f => f.value === obs.follow_up);

  return (
    <div className="glass-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-800">{fmtDate(obs.observation_date)}</p>
          <p className="text-sm text-slate-500 mt-0.5">
            Observed by <span className="font-medium text-slate-700">{obs.observer_name || '—'}</span>
            {obs.observer_designation && <span className="text-slate-400"> · {obs.observer_designation}</span>}
          </p>
          {(obs.subject_theme || obs.grade) && (
            <p className="text-xs text-slate-400 mt-0.5">
              {obs.subject_theme}{obs.grade && ` · Grade ${obs.grade}`}{obs.section && ` ${obs.section}`}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {avg && <span className="text-xl font-bold text-primary-600">{avg}<span className="text-xs font-normal text-slate-400">/5</span></span>}
          {obs.overall_rating && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${ratingCls}`}>{obs.overall_rating}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {OBS_CRITERIA.map(c => {
          const val = obs[c.key];
          const color = val > 4 ? 'text-emerald-600' : val > 3 ? 'text-blue-600' : val > 2 ? 'text-amber-600' : val ? 'text-red-500' : 'text-slate-300';
          return (
            <div key={c.key} className="text-center p-2 bg-slate-50 rounded-lg">
              <p className={`text-base font-bold ${color}`}>{val || '—'}</p>
              <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{c.label}</p>
            </div>
          );
        })}
      </div>

      {(obs.strengths || obs.areas_for_development || obs.suggestions || obs.notes) && (
        <div className="space-y-2 pt-3 border-t border-slate-100">
          {obs.strengths && (
            <div className="p-2.5 bg-emerald-50 rounded-lg">
              <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1">Strengths</p>
              <p className="text-sm text-slate-700">{obs.strengths}</p>
            </div>
          )}
          {obs.areas_for_development && (
            <div className="p-2.5 bg-amber-50 rounded-lg">
              <p className="text-[11px] font-bold text-amber-700 uppercase tracking-wide mb-1">Areas for Development</p>
              <p className="text-sm text-slate-700">{obs.areas_for_development}</p>
            </div>
          )}
          {obs.suggestions && (
            <div className="p-2.5 bg-blue-50 rounded-lg">
              <p className="text-[11px] font-bold text-blue-700 uppercase tracking-wide mb-1">Suggestions</p>
              <p className="text-sm text-slate-700">{obs.suggestions}</p>
            </div>
          )}
          {obs.notes && (
            <div className="p-2.5 bg-slate-50 rounded-lg">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">Additional Notes</p>
              <p className="text-sm text-slate-700">{obs.notes}</p>
            </div>
          )}
        </div>
      )}

      {obs.follow_up && obs.follow_up !== 'none' && (
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <AlertTriangle size={14} className="text-amber-500 shrink-0" />
          <span className="text-xs text-amber-700">
            Follow-up: <strong>{followUp?.label || obs.follow_up}</strong>
          </span>
        </div>
      )}
    </div>
  );
}

function MyObservationsPanel({ data, loading }) {
  if (loading) return <PageLoader />;
  if (!data) return null;

  if (!data.found) return (
    <div className="glass-card p-10 text-center">
      <Award size={40} className="mx-auto mb-3 text-slate-300" />
      <p className="font-semibold text-slate-600">No employee record linked to your account.</p>
      <p className="text-sm text-slate-400 mt-1">Contact the admin to link your employee profile.</p>
    </div>
  );

  const obs = data.observations || [];
  if (!obs.length) return (
    <div className="glass-card p-10 text-center">
      <Award size={40} className="mx-auto mb-3 text-slate-300" />
      <p className="font-semibold text-slate-600">No published observations about you yet.</p>
      <p className="text-sm text-slate-400 mt-1">Observations will appear here once published by your observer.</p>
    </div>
  );

  const scored  = obs.filter(o => o.computed_avg);
  const avgScore = scored.length ? scored.reduce((s, o) => s + Number(o.computed_avg), 0) / scored.length : null;
  const latestRating = obs[0]?.overall_rating || null;
  const pendingFollowup = obs.filter(o => o.follow_up && o.follow_up !== 'none').length;

  return (
    <div className="space-y-4">
      <div className="glass-card p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-4">My Observation Summary — {data.employee?.name}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="text-center p-3 bg-primary-50 rounded-xl">
            <p className="text-2xl font-bold text-primary-600">{obs.length}</p>
            <p className="text-xs text-slate-500 mt-1">Total Observations</p>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-xl">
            <p className="text-2xl font-bold text-emerald-600">{avgScore != null ? avgScore.toFixed(2) : '—'}</p>
            <p className="text-xs text-slate-500 mt-1">Avg Score / 5</p>
          </div>
          <div className="text-center p-3 bg-indigo-50 rounded-xl">
            <p className={`text-lg font-bold capitalize ${RATING_COLORS[latestRating] || ''} px-2 py-0.5 rounded-full border inline-block`}>
              {latestRating || '—'}
            </p>
            <p className="text-xs text-slate-500 mt-1">Latest Rating</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-xl">
            <p className={`text-2xl font-bold ${pendingFollowup > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{pendingFollowup}</p>
            <p className="text-xs text-slate-500 mt-1">Pending Follow-ups</p>
          </div>
        </div>
      </div>

      {obs.map(o => <ObsAboutMeCard key={o.id} obs={o} />)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════
export default function TeacherObservation() {
  const { yearId } = useContext(YearContext);
  const { canEdit, user, isAdmin, isSuperAdmin, isManagement: isMgmtRole } = useAuth();
  // management role sees the full admin-style dashboard (all school data, read-only)
  const isManagement = isAdmin || isSuperAdmin || isMgmtRole;
  // only true admins can access the Setup tab
  const canSetup     = isAdmin || isSuperAdmin;
  // class/subject teachers are the *observed* party — their home view is "My Feedback"
  const normalizedRole = (user?.role || '').toLowerCase().replace(/[\s-]+/g, '_');
  const isTeacherOnly  = !isManagement && ['class_teacher', 'subject_teacher'].includes(normalizedRole);
  const isCoordinator  = normalizedRole === 'coordinator';
  const [isObserver, setIsObserver] = useState(isManagement);
  // Only observers (is_observer=1 in employees) or admins can create/edit observations
  const canCreateObs   = canEdit || isObserver;

  useEffect(() => {
    if (isManagement) return; // admins always have access
    observationsAPI.getMyObserverInfo()
      .then(r => setIsObserver(!!r.data.is_observer))
      .catch(() => {});
  }, [isManagement]);

  // ── Tab state — teacher roles open on My Feedback by default ──
  const [activeTab, setActiveTab] = useState(isTeacherOnly ? 'about-me' : 'dashboard');

  // ── Dashboard overview data (loaded once per yearId) ───────
  const [dashData,    setDashData]    = useState(null);
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    setDashData(null);
    setPendingData(null);
    Promise.all([
      observationsAPI.getDashboard({ yearId }),
      observationsAPI.getPending({ yearId }),
    ]).then(([d, p]) => {
      setDashData(d.data);
      setPendingData(p.data);
    }).catch(() => {});
  }, [yearId]);

  // ── My Feedback ("about me") data for non-management users ──
  const [aboutMeData,    setAboutMeData]    = useState(null);
  const [aboutMeLoading, setAboutMeLoading] = useState(false);

  useEffect(() => {
    if (isManagement || activeTab !== 'about-me') return;
    setAboutMeLoading(true);
    observationsAPI.getAboutMe({ yearId })
      .then(r => setAboutMeData(r.data))
      .catch(() => setAboutMeData({ found: false, observations: [] }))
      .finally(() => setAboutMeLoading(false));
  }, [activeTab, yearId, isManagement]);

  // ── Observations list state ────────────────────────────────
  const [stats,        setStats]        = useState(null);
  const [records,      setRecords]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [printObs,     setPrintObs]     = useState(null);
  const [fTeacher,     setFTeacher]     = useState('');
  const [fObserver,    setFObserver]    = useState('');
  const [fFrom,        setFFrom]        = useState('');
  const [fTo,          setFTo]          = useState('');
  const [fStatus,      setFStatus]      = useState('published');
  const [observers,    setObservers]    = useState([]);
  const [sortField,    setSortField]    = useState('date');
  const [sortDir,      setSortDir]      = useState('desc');

  // ── PDF download state ─────────────────────────────────────
  const [pdfObs,       setPdfObs]       = useState(null);
  const [pdfGenerating,setPdfGenerating]= useState(false);
  // ── View observation modal ─────────────────────────────────
  const [viewObs,      setViewObs]      = useState(null);
  const [viewLoading,  setViewLoading]  = useState(false);
  // ── KPI summary drawer ─────────────────────────────────────
  const [kpiSummary,   setKpiSummary]   = useState(null);
  const [schoolLogoUrl,setSchoolLogoUrl]= useState(null);
  const pdfContainerRef = useRef(null);

  // ── Trigger PDF capture after PDFReportContent renders ─────
  useEffect(() => {
    if (!pdfObs) return;
    const toastId = toast.loading('Generating PDF…');
    const timer = setTimeout(async () => {
      try {
        const { default: html2canvas } = await import('html2canvas');
        const { jsPDF } = await import('jspdf');

        const element = pdfContainerRef.current;
        if (!element) { toast.error('Render failed', { id: toastId }); return; }

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: 794,
          windowWidth: 794,
        });

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pdfW = pdf.internal.pageSize.getWidth();   // 210mm
        const pdfH = pdf.internal.pageSize.getHeight();  // 297mm
        const imgH = (canvas.height * pdfW) / canvas.width;

        // Strict 2-page slice — add next page only when meaningful content remains
        // (> 1mm threshold eliminates floating-point ghost pages)
        let heightLeft = imgH;
        let position  = 0;
        pdf.addImage(imgData, 'PNG', 0, position, pdfW, imgH);
        heightLeft -= pdfH;
        while (heightLeft > 1) {
          position -= pdfH;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, pdfW, imgH);
          heightLeft -= pdfH;
        }

        const tName = (pdfObs.teacher_name_db || pdfObs.teacher_name || 'obs').replace(/[^a-zA-Z0-9]/g, '_');
        const dDate = pdfObs.observation_date?.split('T')[0] || 'date';
        pdf.save(`TKS_Observation_${tName}_${dDate}.pdf`);
        toast.success('PDF downloaded!', { id: toastId });
      } catch (err) {
        console.error('PDF error:', err);
        toast.error('PDF generation failed', { id: toastId });
      } finally {
        setPdfObs(null);
        setPdfGenerating(false);
      }
    }, 450); // wait for DOM render
    return () => clearTimeout(timer);
  }, [pdfObs]); // eslint-disable-line

  // ── Load school logo as data-URL so html2canvas can embed it ─
  useEffect(() => {
    schoolConfigAPI.getConfig().then(r => {
      if (r.data?.school_logo) {
        const { protocol, hostname } = window.location;
        const imgUrl = `${protocol}//${hostname}:5000${r.data.school_logo}`;
        fetch(imgUrl)
          .then(res => res.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onload = () => setSchoolLogoUrl(reader.result);
            reader.readAsDataURL(blob);
          })
          .catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const openDownloadPDF = async (id) => {
    if (pdfGenerating) return;
    setPdfGenerating(true);
    try {
      const r = await observationsAPI.getOne(id);
      setPdfObs(r.data); // triggers useEffect above
    } catch {
      toast.error('Failed to load observation');
      setPdfGenerating(false);
    }
  };

  const openView = async (id) => {
    setViewLoading(true);
    try {
      const r = await observationsAPI.getOne(id);
      setViewObs(r.data);
    } catch {
      toast.error('Failed to load observation');
    } finally {
      setViewLoading(false);
    }
  };

  const todayISO      = () => new Date().toISOString().split('T')[0];
  const weekStartISO  = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); return d.toISOString().split('T')[0]; };
  const monthStartISO = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; };

  const openObsSummary = async (title, apiParams = {}, clientFilter = null) => {
    setKpiSummary({ title, type: 'observations', records: [], loading: true });
    try {
      const r = await observationsAPI.getAll({ yearId, status: 'published', limit: 200, ...apiParams });
      let recs = r.data.data || [];
      if (clientFilter) recs = recs.filter(clientFilter);
      setKpiSummary({ title, type: 'observations', records: recs, loading: false });
    } catch {
      toast.error('Failed to load summary');
      setKpiSummary(null);
    }
  };

  const openTeacherSummary = async () => {
    setKpiSummary({ title: 'Teachers Observed', type: 'teachers', records: [], loading: true });
    try {
      const r = await observationsAPI.getByTeacher({ yearId });
      setKpiSummary({ title: 'Teachers Observed', type: 'teachers', records: r.data || [], loading: false });
    } catch {
      toast.error('Failed to load summary');
      setKpiSummary(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = {
      yearId,
      teacher: fTeacher || undefined,
      from:    fFrom    || undefined,
      to:      fTo      || undefined,
    };
    if (isTeacherOnly) {
      params.myOnly = 'true';
    }
    if (isManagement) {
      if (fObserver) params.observer = fObserver;
      params.status = fStatus || 'published';
    }

    try {
      const r = await observationsAPI.getAll(params);
      setRecords(r.data.data || []);
    } catch(e) {
      console.error('getAll error:', e);
      setRecords([]);
    }

    observationsAPI.getStats({ yearId }).then(s => setStats(s.data)).catch(e => console.error('stats error:', e));
    observationsAPI.getObservers({ yearId }).then(r => setObservers(r.data || [])).catch(e => console.error('observers error:', e));
    setLoading(false);
  }, [yearId, fTeacher, fObserver, fFrom, fTo, fStatus, isManagement]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try { await observationsAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
    setConfirmDel(null);
  };

  const handlePublish = async (id, currentStatus) => {
    try {
      if (currentStatus === 'published') {
        await observationsAPI.unpublish(id);
        toast.success('Reverted to draft');
      } else {
        await observationsAPI.publish(id);
        toast.success('✅ Published!');
      }
      load();
    } catch(e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const openEdit = async (id) => {
    try { const r = await observationsAPI.getOne(id); setEditing(r.data); setShowForm(true); }
    catch { toast.error('Failed to load'); }
  };

  const openPrint = async (id) => {
    try { const r = await observationsAPI.getOne(id); setPrintObs(r.data); }
    catch { toast.error('Failed to load'); }
  };

  const handleExport = async () => {
    try {
      const r = await observationsAPI.export({ yearId });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `observations_${yearId}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const inp = 'text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400';

  // ── Sort handler ───────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const RATING_ORDER = { excelling: 5, proficient: 4, achieving: 3, meeting: 2, developing: 1 };

  const sortedRecords = [...records].sort((a, b) => {
    let aVal, bVal;
    switch (sortField) {
      case 'teacher':
        aVal = (a.teacher_name_db || a.teacher_name || '').toLowerCase();
        bVal = (b.teacher_name_db || b.teacher_name || '').toLowerCase();
        break;
      case 'observer':
        aVal = (a.observer_name_db || a.observer_name || '').toLowerCase();
        bVal = (b.observer_name_db || b.observer_name || '').toLowerCase();
        break;
      case 'date':
        aVal = a.observation_date || '';
        bVal = b.observation_date || '';
        break;
      case 'subject':
        aVal = (a.subject_theme || '').toLowerCase();
        bVal = (b.subject_theme || '').toLowerCase();
        break;
      case 'curriculum':
        aVal = (a.curriculum || '').toLowerCase();
        bVal = (b.curriculum || '').toLowerCase();
        break;
      case 'rating':
        aVal = RATING_ORDER[a.overall_rating] || 0;
        bVal = RATING_ORDER[b.overall_rating] || 0;
        break;
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      default: return 0;
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Tab definitions
  // Teacher-only roles lead with My Feedback; observer/admin roles lead with Dashboard
  const TABS = isTeacherOnly
    ? [
        { id: 'about-me',     label: 'My Feedback',  icon: Award     },
        { id: 'observations', label: 'Observations',  icon: FileText  },
        { id: 'dashboard',    label: 'Dashboard',     icon: BarChart2 },
      ]
    : [
        { id: 'dashboard',    label: 'Dashboard',     icon: BarChart2 },
        { id: 'observations', label: 'Observations',  icon: FileText  },
        ...(!isManagement ? [{ id: 'about-me', label: 'My Feedback', icon: Award }] : []),
        ...(canSetup ? [{ id: 'setup', label: 'Setup', icon: Settings }] : []),
      ];

  return (
    <div className="space-y-5 animate-fade-in">

      {/* Hidden PDF render target */}
      {pdfObs && (
        <div
          ref={pdfContainerRef}
          style={{
            position: 'fixed', top: 0, left: '-820px',
            width: '794px', height: '2246px', overflow: 'hidden', zIndex: -1, backgroundColor: '#ffffff',
          }}
        >
          <PDFReportContent obs={pdfObs} logoUrl={schoolLogoUrl} />
        </div>
      )}

      {/* PrintView overlay */}
      {printObs && <PrintView obs={printObs} onClose={() => setPrintObs(null)}/>}

      {/* View observation modal */}
      {viewObs && (
        <ViewObservationModal
          obs={viewObs}
          logoUrl={schoolLogoUrl}
          onClose={() => setViewObs(null)}
          onDownload={() => openDownloadPDF(viewObs.id)}
          pdfGenerating={pdfGenerating}
        />
      )}

      {/* KPI summary drawer */}
      {kpiSummary && (
        <KpiSummaryDrawer
          summary={kpiSummary}
          onClose={() => setKpiSummary(null)}
          onViewObs={obs => { setKpiSummary(null); openView(obs.id); }}
          onTeacherClick={t => openObsSummary(
            `${t.teacher_name} — Observations`,
            {},
            obs => (obs.teacher_name_db || obs.teacher_name) === t.teacher_name
          )}
        />
      )}

      {/* ── Page header with inline tab navigation ─────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-slate-800">Teacher Observations</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            IB PYP · IGCSE · ICSE · Academic Year 2026–27
            {!isManagement && <span className="ml-2 text-primary-600 font-medium">· Showing your observations</span>}
          </p>
        </div>

        {/* Tabs + action buttons — all in one row */}
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {/* Tab switcher */}
          <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <tab.icon size={13}/>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          {isManagement && (
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-sm text-slate-600 rounded-lg hover:bg-slate-50">
              <Download size={14}/> Export
            </button>
          )}
          {canCreateObs && (
            <button onClick={() => { setEditing(null); setShowForm(true); }}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-xl shadow-sm transition-colors">
              <Plus size={15}/> New Observation
            </button>
          )}
        </div>
      </div>

      {/* ── Admin: KPI stat cards ────────────────────────────── */}
      {isManagement && activeTab === 'dashboard' && (
        <div className="space-y-4">
          {/* Row 1: Total Observations, Teachers Observed, This Month, This Week */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard title="Total Observations" value={stats?.total         ?? 0} icon={FileText}  color="blue"   loading={!stats} onClick={() => openObsSummary('All Observations')} />
            <StatCard title="Teachers Observed"  value={stats?.totalTeachers ?? 0} icon={Users}     color="purple" loading={!stats} onClick={() => openTeacherSummary()} />
            <StatCard title="This Month"         value={dashData?.thisMonth  ?? 0} icon={BarChart2} color="teal"   loading={!stats} onClick={() => openObsSummary('This Month', { from: monthStartISO(), to: todayISO() })} />
            <StatCard title="This Week"          value={dashData?.thisWeek   ?? 0} icon={Activity}  color="blue"   loading={!stats} onClick={() => openObsSummary('This Week', { from: weekStartISO(), to: todayISO() })} />
          </div>

          {/* Row 2: Rating distribution + Last Observation */}
          {stats?.ratingDist?.filter(r => r.overall_rating).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {stats.ratingDist.filter(r => r.overall_rating).map(r => {
                const colorMap = { excelling: 'green', proficient: 'blue', achieving: 'purple', meeting: 'yellow', developing: 'red' };
                const label = r.overall_rating.charAt(0).toUpperCase() + r.overall_rating.slice(1);
                return (
                  <StatCard key={r.overall_rating} title={r.overall_rating} value={r.cnt} icon={Award}
                    color={colorMap[r.overall_rating] || 'blue'}
                    onClick={() => openObsSummary(`${label} Observations`, {}, obs => obs.overall_rating === r.overall_rating)} />
                );
              })}
              <StatCard title="Last Observation" value={fmtDate(stats?.lastObservation)} icon={Clock} color="teal" loading={!stats}
                onClick={() => { const d = stats?.lastObservation?.split('T')[0]; if (d) openObsSummary('Last Observation', { from: d, to: d }); }} />
            </div>
          )}
        </div>
      )}

      {/* ── Tab panels ──────────────────────────────────────── */}

      {/* Dashboard tab */}
      {activeTab === 'dashboard' && (
        <ObsDashboard
          yearId={yearId}
          isManagement={isManagement}
          preloadedDash={dashData}
          preloadedPending={pendingData}
        />
      )}

      {/* Observations tab */}
      {activeTab === 'observations' && (
        <div className="space-y-4">



          {/* Filters */}
          <div className="glass-card p-4 flex flex-wrap gap-3 items-center">
            <input className={inp} placeholder="Search teacher…" value={fTeacher} onChange={e => setFTeacher(e.target.value)}/>
            {isManagement && (
              <select className={inp} value={fObserver} onChange={e => setFObserver(e.target.value)}>
                <option value="">All observers</option>
                {observers.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
            <input type="date" className={inp} value={fFrom} onChange={e => setFFrom(e.target.value)}/>
            <span className="text-slate-400 text-sm">to</span>
            <input type="date" className={inp} value={fTo} onChange={e => setFTo(e.target.value)}/>
            {isManagement && (
              <select className={inp} value={fStatus} onChange={e => setFStatus(e.target.value)}>
                <option value="published">Published only</option>
                <option value="draft">Drafts only</option>
                <option value="all">All</option>
              </select>
            )}
            <button onClick={() => { setFTeacher(''); setFObserver(''); setFFrom(''); setFTo(''); setFStatus('published'); }}
              className="text-xs text-slate-400 hover:text-slate-600 underline">Clear</button>
          </div>

          {/* Table */}
          {loading ? <PageLoader/> : (
            <div className="glass-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs text-slate-500 uppercase font-medium">
                    <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('teacher')}>
                      Teacher<SortArrow active={sortField === 'teacher'} dir={sortDir}/>
                    </th>
                    {isManagement && (
                      <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('observer')}>
                        Observer<SortArrow active={sortField === 'observer'} dir={sortDir}/>
                      </th>
                    )}
                    <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('date')}>
                      Date<SortArrow active={sortField === 'date'} dir={sortDir}/>
                    </th>
                    <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('subject')}>
                      Subject / Grade<SortArrow active={sortField === 'subject'} dir={sortDir}/>
                    </th>
                    <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('curriculum')}>
                      Curriculum<SortArrow active={sortField === 'curriculum'} dir={sortDir}/>
                    </th>
                    <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('rating')}>
                      Overall Rating<SortArrow active={sortField === 'rating'} dir={sortDir}/>
                    </th>
                    {isManagement && (
                      <th className="px-5 py-3 text-left cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap" onClick={() => handleSort('status')}>
                        Status<SortArrow active={sortField === 'status'} dir={sortDir}/>
                      </th>
                    )}
                    <th className="px-5 py-3"/>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {sortedRecords.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-12 text-slate-400">
                      <FileText size={32} className="mx-auto mb-2 opacity-30"/>
                      {isManagement ? 'No observations match the current filters.' : 'You have no observations yet.'}
                    </td></tr>
                  ) : sortedRecords.map(r => (
                    <tr key={r.id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3 font-medium text-slate-700">{r.teacher_name_db || r.teacher_name || '—'}</td>
                      {isManagement && <td className="px-5 py-3 text-slate-600">{r.observer_name_db || r.observer_name || '—'}</td>}
                      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtDate(r.observation_date)}</td>
                      <td className="px-5 py-3 text-slate-600 text-xs">
                        {r.subject_theme && <p>{r.subject_theme}</p>}
                        {(r.grade || r.section) && (
                          <p className="text-slate-400">{[r.grade, r.section ? `Sec ${r.section}` : ''].filter(Boolean).join(' / ')}</p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {r.curriculum && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{r.curriculum}</span>}
                      </td>
                      <td className="px-5 py-3">
                        {r.overall_rating ? (
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${RATING_COLORS[r.overall_rating] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                              {r.overall_rating}
                            </span>
                            {r.computed_avg && <span className="text-[10px] text-slate-400">{Number(r.computed_avg).toFixed(1)}/5</span>}
                          </div>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      {isManagement && (
                        <td className="px-5 py-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                            r.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                            {r.status === 'published' ? '✓ Published' : 'Draft'}
                          </span>
                        </td>
                      )}
                      <td className="px-5 py-3">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openView(r.id)} title="View observation"
                            disabled={viewLoading}
                            className="p-1.5 hover:bg-violet-50 text-violet-500 rounded-lg transition-colors disabled:opacity-40">
                            <Eye size={14}/>
                          </button>
                          {r.status === 'published' && (
                            <button onClick={() => openDownloadPDF(r.id)} title="Download PDF"
                              disabled={pdfGenerating}
                              className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors disabled:opacity-40">
                              <Download size={14}/>
                            </button>
                          )}
                          {/* Publish (draft → published): any canEdit user */}
                          {canEdit && r.status !== 'published' && (
                            <button onClick={() => handlePublish(r.id, r.status)}
                              title="Publish"
                              className="p-1.5 rounded-lg transition-colors hover:bg-emerald-50 text-emerald-600">
                              <Send size={14}/>
                            </button>
                          )}
                          {/* Revert to draft: admin / super-admin only */}
                          {canSetup && r.status === 'published' && (
                            <button onClick={() => handlePublish(r.id, r.status)}
                              title="Revert to draft"
                              className="p-1.5 rounded-lg transition-colors hover:bg-amber-50 text-amber-500">
                              <RotateCcw size={14}/>
                            </button>
                          )}
                          {/* Edit / Delete: admin always · observer only while draft */}
                          {canEdit && (canSetup || r.status !== 'published') && (
                            <>
                              <button onClick={() => openEdit(r.id)} className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg" title="Edit"><Edit2 size={14}/></button>
                              <button onClick={() => setConfirmDel(r.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg" title="Delete"><Trash2 size={14}/></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Setup tab (admin only) */}
      {activeTab === 'setup' && canSetup && (
        <div className="space-y-5">
          <ObsSetup yearId={yearId}/>
          <EmailNotificationSetup    module="teacherObservations" moduleLabel="Teacher Observations" yearId={yearId} />
          <TelegramNotificationSetup module="teacherObservations" moduleLabel="Teacher Observations" yearId={yearId} />
        </div>
      )}

      {/* My Feedback tab */}
      {activeTab === 'about-me' && !isManagement && (
        <MyObservationsPanel data={aboutMeData} loading={aboutMeLoading} />
      )}

      {/* Observation form modal */}
      {showForm && <ObservationForm initial={editing} onSave={() => { setShowForm(false); setEditing(null); load(); }} onCancel={() => { setShowForm(false); setEditing(null); }}/>}

      {/* Delete confirmation */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <p className="text-sm text-slate-700 mb-4">Delete this observation? This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDelete(confirmDel)} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
